import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { RESOURCE_CATEGORIES, RESOURCE_TYPES } from '../utils/constants';
import { adminDeleteStorageObjects, adminDeleteProfileFiles } from '../utils/adminStorage';
import { adminInvoke, adminRpc, adminWhoami, ADMIN_FORBIDDEN } from '../utils/adminApi';

const MAINTENANCE_KEY = 'uxo_maintenance_mode';

// Open a tech document in a new tab.
//
// uxo-uploads is private and every SELECT policy on it is scoped to the owning
// technician, so supabase.storage.createSignedUrl() cannot work here even with
// an admin session -- an admin is not the owner of anyone else's path. Storage
// reports that RLS denial as a 404, which historically made this look like a
// missing file. The edge function verifies the admin against admin_users and
// signs with the service-role key instead.
async function openAdminDocument(filePath, { download = false } = {}) {
  if (!filePath) return { error: 'This document has no stored file path.' };

  // Opened synchronously: browsers block window.open() from an async callback
  // that has already awaited, so the tab is claimed first and navigated after.
  const tab = window.open('', '_blank');

  const { data, error } = await adminInvoke('admin-sign-document-url', {
    file_path: filePath,
    download,
  });

  if (error || !data?.signedUrl) {
    if (tab) tab.close();
    const detail = error?.message || data?.error || 'Unknown error';
    console.error('admin-sign-document-url failed:', detail);
    return { error: `Could not open document: ${detail}` };
  }

  if (tab) tab.location.href = data.signedUrl;
  else window.open(data.signedUrl, '_blank'); // popup blocked; try direct
  return {};
}

// ─── Admin Login Screen ──────────────────────────────────────────────────────
//
// A real Supabase sign-in, not a shared password compared in the browser.
//
// The old gate checked `password === REACT_APP_ADMIN_PASSWORD` -- a constant
// baked into the bundle -- and recorded the result in localStorage. Neither the
// comparison nor the flag proved anything to the server: the admin key rode
// along on every call regardless, so setting uxo_admin_session by hand in
// devtools was enough to work the dashboard. Access is now a session JWT the
// gateway checks against admin_users, and this screen only obtains one.
function AdminLogin({ onLogin, notice }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(notice || '');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');

    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (signInErr) {
      // Deliberately generic: a message distinguishing "no such account" from
      // "wrong password" would confirm which addresses hold admin accounts.
      setError('Incorrect email or password.');
      setBusy(false);
      return;
    }

    // Signing in is not the same as being an admin. Only the gateway can answer
    // that -- the browser cannot read admin_users.
    const { data, error: whoErr } = await adminWhoami();
    if (whoErr) {
      // Do not leave the session behind. It would not open the dashboard, but
      // it would quietly sign them into the rest of the site as an ordinary
      // user, which is not what anyone typing into an admin form intended.
      await supabase.auth.signOut();
      setError(
        whoErr.status === ADMIN_FORBIDDEN
          ? 'That account does not have admin access.'
          : whoErr.message
      );
      setBusy(false);
      return;
    }

    onLogin(data?.email || email.trim());
  };

  return (
    <div style={s.root}>
      <div style={s.loginWrap}>
        <div style={s.loginCard}>
          <div style={{ fontSize: 32, marginBottom: 16, textAlign: 'center' }}>{"\uD83D\uDD12"}</div>
          <h1 style={{ fontSize: 24, fontWeight: 'bold', margin: '0 0 8px', textAlign: 'center' }}>Admin Access</h1>
          <p style={{ color: '#7a7570', fontSize: 14, margin: '0 0 24px', textAlign: 'center' }}>
            Sign in with your admin account to continue.
          </p>
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              style={{ ...s.input, marginBottom: 10 }}
              placeholder="Email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              autoComplete="username"
              autoFocus
            />
            <div style={s.passwordWrap}>
              <input
                type={showPassword ? 'text' : 'password'}
                style={{ ...s.input, flex: 1, width: 'auto', paddingRight: 48 }}
                placeholder="Password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={s.eyeBtn}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {error && <div style={s.errorMsg}>{error}</div>}
            <button
              type="submit"
              disabled={busy}
              style={{ ...s.btnPrimary, width: '100%', marginTop: 16, ...(busy ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
            >
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirmation Dialog ──────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel, requireTypedConfirmation }) {
  const [typedValue, setTypedValue] = useState('');
  const isBlocked = requireTypedConfirmation && typedValue !== requireTypedConfirmation;

  return (
    <div style={s.overlay}>
      <div style={{
        ...s.dialog,
        ...(requireTypedConfirmation ? { border: '1px solid #dc2626', boxShadow: '0 0 30px rgba(220,38,38,0.2)' } : {}),
      }}>
        {requireTypedConfirmation && (
          <div style={{ background: '#2a0a0a', border: '1px solid #8B0000', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{"\u26A0\uFE0F"}</span>
            <span style={{ color: '#ff6b6b', fontSize: 13, fontWeight: 'bold' }}>PAID ACCOUNT - DESTRUCTIVE ACTION</span>
          </div>
        )}
        <p style={{ color: '#e8e4dc', fontSize: 15, lineHeight: 1.6, margin: '0 0 20px' }}>{message}</p>
        {requireTypedConfirmation && (
          <div style={{ margin: '0 0 20px' }}>
            <label style={{ display: 'block', color: '#9a9490', fontSize: 13, marginBottom: 8 }}>
              Type <strong style={{ color: '#ff6b6b' }}>{requireTypedConfirmation}</strong> to confirm:
            </label>
            <input
              type="text"
              value={typedValue}
              onChange={e => setTypedValue(e.target.value)}
              placeholder={requireTypedConfirmation}
              autoFocus
              style={{ width: '100%', padding: '8px 12px', background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 6, color: '#e8e4dc', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button style={s.btnSecondary} onClick={onCancel}>Cancel</button>
          <button
            style={{ ...s.btnDanger, ...(isBlocked ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
            onClick={onConfirm}
            disabled={isBlocked}
          >
            {confirmLabel || 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Admin Dashboard ────────────────────────────────────────────────────
export default function AdminDashboard() {
  // null = still asking the gateway, false = show the login form, true = admin.
  // The third state matters: without it the login form flashes on every reload
  // for an admin who is already signed in, and worse, a reload during a slow
  // whoami looks like a failed session.
  const [authed, setAuthed] = useState(null);
  const [adminEmail, setAdminEmail] = useState(null);
  const [gateNotice, setGateNotice] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  // Data states
  const [users, setUsers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [jobPosts, setJobPosts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);

  // UI states — the gate renders its own screen until `authed` resolves, and
  // the tab effect below turns this on the moment it does.
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Bulk selection states
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkSuccess, setBulkSuccess] = useState(null);

  // Inline edit states
  const [editingHours, setEditingHours] = useState(null); // { id, value }
  const [editingBalance, setEditingBalance] = useState(null); // { employerId, value }

  // User controls states
  const [messagingUser, setMessagingUser] = useState(null); // { userId, email, name }
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Logbook verification queue states
  const [logbookQueue, setLogbookQueue] = useState([]);
  const [logbookQueueLoading, setLogbookQueueLoading] = useState(false);
  const [verifyingTech, setVerifyingTech] = useState(null); // tech id being verified
  const [verifyHoursInput, setVerifyHoursInput] = useState({});
  const [verifySupervisoryHoursInput, setVerifySupervisoryHoursInput] = useState({});
  const [verifyNotesInput, setVerifyNotesInput] = useState({});

  // Quality credential verification queue states
  const [qualityQueue, setQualityQueue] = useState([]);
  const [qualityQueueLoading, setQualityQueueLoading] = useState(false);
  const [qualityNotesInput, setQualityNotesInput] = useState({});

  // Platform controls states
  const [maintenanceMode, setMaintenanceMode] = useState(() => localStorage.getItem(MAINTENANCE_KEY) === 'true');
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastConfirm, setBroadcastConfirm] = useState(null); // 'techs' | 'employers' | null
  const [exportingTechs, setExportingTechs] = useState(false);
  const [exportingEmployers, setExportingEmployers] = useState(false);

  // Verification badge counts. Declared here, above the gate's early return,
  // because hooks placed after it are skipped while the login form is showing
  // and then appear when it is not -- React throws "rendered more hooks than
  // during the previous render" on exactly that transition.
  const [logbookPendingCount, setLogbookPendingCount] = useState(0);
  const [qualityPendingCount, setQualityPendingCount] = useState(0);
  useEffect(() => {
    if (!authed) return;
    // Counted server-side via RPC: a direct count is filtered by the
    // open_to_work public read policy and undercounts the real queue.
    adminRpc('admin_verification_counts')
      .then(({ data, error }) => {
        if (error) { console.error('admin_verification_counts failed:', error); return; }
        setLogbookPendingCount(data?.logbook || 0);
        setQualityPendingCount(data?.quality || 0);
      });
  }, [authed, activeTab]);

  // ── Admin gate ─────────────────────────────────────────────────────────────
  // Ask the gateway whether this browser's session is an admin. Nothing renders
  // and no data is fetched until it answers.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data, error } = await adminWhoami();
      if (cancelled) return;

      if (error) {
        // 403 means a real session that is not on the allowlist. Clear it for
        // the same reason AdminLogin does -- and so a stale non-admin session
        // cannot sit in front of the form forever.
        if (error.status === ADMIN_FORBIDDEN) {
          await supabase.auth.signOut();
          if (!cancelled) setGateNotice('That account does not have admin access.');
        }
        if (!cancelled) setAuthed(false);
        return;
      }

      setAdminEmail(data?.email || null);
      setAuthed(true);
    })();

    // Covers sign-out in another tab and a refresh token that fails to renew:
    // the session is gone, so every call from here on would 401 anyway. Drop to
    // the login form rather than letting the dashboard error its way there.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setAuthed(false);
        setAdminEmail(null);
      }
    });

    return () => { cancelled = true; subscription.unsubscribe(); };
  }, []);

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await adminRpc('admin_list_users');
    if (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  }, []);

  const fetchTechs = useCallback(async () => {
    setLoading(true);
    // Via the RPC, not the table: tech_profiles' public SELECT policy is
    // USING (open_to_work = true) (migration 000), so reading it directly from
    // the browser silently dropped every tech not open to work. An admin
    // session does not change that -- no policy grants admins a wider view.
    const { data, error } = await adminRpc('admin_list_tech_profiles');
    if (error) {
      console.error('admin_list_tech_profiles failed:', error);
      setActionError(`Failed to load tech profiles: ${error.message}`);
      setTechs([]);
    } else {
      setTechs(data || []);
    }
    setLoading(false);
  }, []);

  const fetchEmployers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await adminRpc('admin_list_employers');
    if (error) {
      console.error('Failed to fetch employers:', error);
      setEmployers([]);
    } else {
      setEmployers((data || []).map(e => ({
        ...e,
        balance: (e.current_balance != null || e.monthly_allowance != null)
          ? { current_balance: e.current_balance, monthly_allowance: e.monthly_allowance }
          : null,
      })));
    }
    setLoading(false);
  }, []);

  const fetchJobPosts = useCallback(async () => {
    setLoading(true);
    // applicant_count now comes from the RPC. Counting it client-side meant
    // reading applications from the browser, which has matched no SELECT policy
    // for a non-participant since migration 006 -- so every job showed 0
    // applicants.
    const { data: posts, error: postsErr } = await adminRpc('admin_list_job_posts');
    if (postsErr) {
      console.error('admin_list_job_posts failed:', postsErr);
      setActionError(`Failed to load job posts: ${postsErr.message}`);
      setJobPosts([]);
    } else {
      setJobPosts(posts || []);
    }
    setLoading(false);
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    const { data, error } = await adminRpc('admin_list_applications');
    if (error) {
      console.error('Failed to fetch applications:', error);
      setApplications([]);
    } else {
      setApplications(data || []);
    }
    setLoading(false);
  }, []);

  const fetchLogbookQueue = useCallback(async () => {
    setLogbookQueueLoading(true);
    // Go through the SECURITY DEFINER RPC (migration 073), not the tables.
    // Reading them directly from the browser hits two RLS walls: tech_documents
    // is readable only by its owning technician (so every submission looked like
    // it had no logbook), and tech_profiles is only publicly readable WHERE
    // open_to_work, which silently hid pending techs who are not open to work.
    const { data, error } = await adminRpc('admin_logbook_queue');
    if (error) {
      console.error('admin_logbook_queue failed:', error);
      setActionError(`Failed to load logbook queue: ${error.message}`);
      setLogbookQueue([]);
      setLogbookQueueLoading(false);
      return;
    }
    setLogbookQueue((data || []).map(t => ({
      ...t,
      logbookDoc: t.logbook_doc || null,
    })));
    setLogbookQueueLoading(false);
  }, []);

  const fetchQualityQueue = useCallback(async () => {
    setQualityQueueLoading(true);
    // Same RLS reasoning as fetchLogbookQueue above.
    const { data, error } = await adminRpc('admin_quality_queue');
    if (error) {
      console.error('admin_quality_queue failed:', error);
      setActionError(`Failed to load quality credential queue: ${error.message}`);
      setQualityQueue([]);
      setQualityQueueLoading(false);
      return;
    }
    setQualityQueue((data || []).map(t => ({
      ...t,
      qualityDoc: t.quality_doc || null,
    })));
    setQualityQueueLoading(false);
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    // The one allowlisted function with no admin_key parameter -- it never had
    // one. It still routes through the gateway: part 2 revokes anon EXECUTE, so
    // the direct call it has used since migration 037 stops working.
    const { data, error } = await adminRpc('admin_dashboard_stats');
    if (error) {
      console.error('Failed to fetch admin stats:', error);
      setStats({
        totalUsers: 0, totalTechs: 0, totalEmployers: 0, totalJobPosts: 0,
        totalUnlocks: 0, totalMessages: 0, totalHires: 0, newSignups: 0,
      });
    } else {
      // Guarded: adminRpc normalises a missing payload to null, and the stats
      // tab should not white-screen on a TypeError if the function ever returns
      // one.
      const d = data || {};
      setStats({
        totalUsers: d.totalUsers || 0,
        totalTechs: d.totalTechs || 0,
        totalEmployers: d.totalEmployers || 0,
        totalJobPosts: d.totalJobPosts || 0,
        totalUnlocks: d.totalUnlocks || 0,
        totalMessages: d.totalMessages || 0,
        totalHires: d.totalHires || 0,
        newSignups: d.newSignups || 0,
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!authed) return;
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'techs') fetchTechs();
    else if (activeTab === 'employers') fetchEmployers();
    else if (activeTab === 'jobs') fetchJobPosts();
    else if (activeTab === 'applications') fetchApplications();
    else if (activeTab === 'stats') fetchStats();
    else if (activeTab === 'logbook') { fetchLogbookQueue(); fetchQualityQueue(); }
  }, [activeTab, authed, fetchUsers, fetchTechs, fetchEmployers, fetchJobPosts, fetchApplications, fetchStats, fetchLogbookQueue, fetchQualityQueue]);

  // ── Logbook verification actions ────────────────────────────────────────────
  const handleVerifyLogbook = async (techId) => {
    setActionError(null);
    const hours = parseInt(verifyHoursInput[techId], 10);
    if (!hours || hours <= 0) {
      setActionError('Enter a valid number of documented MEC hours (greater than 0).');
      return;
    }
    // Supervisory hours must be entered explicitly; 0 is a valid value.
    const supRaw = verifySupervisoryHoursInput[techId];
    if (supRaw === undefined || supRaw === null || supRaw === '') {
      setActionError('Enter documented supervisory hours (enter 0 if none).');
      return;
    }
    const supHours = parseInt(supRaw, 10);
    if (isNaN(supHours) || supHours < 0) {
      setActionError('Documented supervisory hours must be 0 or greater.');
      return;
    }
    setVerifyingTech(techId);
    const notes = verifyNotesInput[techId] || null;
    // tech_profiles UPDATE is own-row only (migration 002), so a direct table
    // write from the dashboard changes nothing regardless of who is signed in.
    // Go through the SECURITY DEFINER RPC (migration 069) like every other
    // admin write.
    // Keys must match the admin_verify_logbook(...) signature exactly —
    // PostgREST resolves the overload by argument name, not position.
    const { error } = await adminRpc('admin_verify_logbook', {
      target_profile_id: techId,
      verified_hours: hours,
      verified_supervisory_hours: supHours,
      notes: notes || null,
    });
    if (error) {
      console.error('admin_verify_logbook failed:', error);
      setActionError(`Failed to mark logbook documented: ${error.message}`);
      setVerifyingTech(null);
      return;
    }
    // Send notification to tech
    supabase.functions.invoke('send-logbook-notification', {
      body: { type: 'verified', tech_profile_id: techId, verified_hours: hours },
    }).catch(() => {});
    setVerifyingTech(null);
    fetchLogbookQueue();
  };

  const handleRejectLogbook = async (techId) => {
    setActionError(null);
    setVerifyingTech(techId);
    const notes = verifyNotesInput[techId] || null;
    const { error } = await adminRpc('admin_reject_logbook', {
      target_profile_id: techId,
      notes,
    });
    if (error) {
      console.error('admin_reject_logbook failed:', error);
      setActionError(`Failed to reject logbook: ${error.message}`);
      setVerifyingTech(null);
      return;
    }
    // Send rejection notification to tech
    supabase.functions.invoke('send-logbook-notification', {
      body: { type: 'rejected', tech_profile_id: techId, notes },
    }).catch(() => {});
    setVerifyingTech(null);
    fetchLogbookQueue();
  };

  // Return for correction — distinct from Reject. Reject means the documents are
  // inadequate; this means they are fine but something needs fixing, so the note
  // explaining what is mandatory rather than optional.
  const handleReturnLogbook = async (techId) => {
    setActionError(null);
    const notes = (verifyNotesInput[techId] || '').trim();
    if (!notes) {
      setActionError('A note is required when returning a submission for correction — tell the tech what to fix.');
      return;
    }
    setVerifyingTech(techId);
    const { error } = await adminRpc('admin_return_logbook', {
      target_profile_id: techId,
      notes,
    });
    if (error) {
      console.error('admin_return_logbook failed:', error);
      setActionError(`Failed to return submission: ${error.message}`);
      setVerifyingTech(null);
      return;
    }
    supabase.functions.invoke('send-logbook-notification', {
      body: { type: 'returned', tech_profile_id: techId, notes },
    }).catch(() => {});
    setVerifyingTech(null);
    fetchLogbookQueue();
  };

  // ── Quality credential verification actions ────────────────────────────────
  // These previously issued a direct UPDATE on tech_profiles, which RLS blocks
  // for anyone but the owning tech. The result was never error-checked, so
  // approving a quality credential silently did nothing. Both now go through
  // the RPCs in migration 073.
  const handleVerifyQuality = async (techId) => {
    setActionError(null);
    setVerifyingTech(techId);
    const notes = qualityNotesInput[techId] || null;
    const { error } = await adminRpc('admin_verify_quality', {
      target_profile_id: techId,
      notes,
    });
    if (error) {
      console.error('admin_verify_quality failed:', error);
      setActionError(`Failed to mark quality credential documented: ${error.message}`);
      setVerifyingTech(null);
      return;
    }
    supabase.functions.invoke('send-logbook-notification', {
      body: { type: 'quality_cert_verified', tech_profile_id: techId },
    }).catch(() => {});
    setVerifyingTech(null);
    fetchQualityQueue();
  };

  const handleRejectQuality = async (techId) => {
    setActionError(null);
    setVerifyingTech(techId);
    const notes = qualityNotesInput[techId] || null;
    const { error } = await adminRpc('admin_reject_quality', {
      target_profile_id: techId,
      notes,
    });
    if (error) {
      console.error('admin_reject_quality failed:', error);
      setActionError(`Failed to reject quality credential: ${error.message}`);
      setVerifyingTech(null);
      return;
    }
    supabase.functions.invoke('send-logbook-notification', {
      body: { type: 'quality_cert_rejected', tech_profile_id: techId, notes },
    }).catch(() => {});
    setVerifyingTech(null);
    fetchQualityQueue();
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  // profileId is what makes accountless rows deletable: a legacy tech profile
  // with no user_accounts row has no user_id to delete by.
  // Storage files are deleted BEFORE the profile row, because
  // admin_delete_user cascades tech_documents away and the paths become
  // unrecoverable. A failed file delete aborts the whole thing rather than
  // leaving orphans behind -- the reconciliation sweep on 2026-08-02 found 42
  // of those, all from deletions that reported success.
  //
  // Storage is NOT covered by the retention carve-out in migration 079: the
  // unlock snapshot holds credential state, not the underlying files.
  const purgeProfileFiles = async (profileId, userId = null) => {
    if (!profileId && !userId) return true;
    const res = await adminDeleteProfileFiles(profileId, userId);
    if (!res.ok) {
      setActionError(`Aborted: the profile's files could not be deleted, so nothing was removed. ${res.error}`);
      setActionLoading(null);
      return false;
    }
    return true;
  };

  const deleteUser = async (userId, profileId = null) => {
    setActionLoading(userId || profileId);
    setActionError(null);
    if (!(await purgeProfileFiles(profileId, userId))) return;
    const { error } = await adminRpc('admin_delete_user', {
      target_user_id: userId || null, target_profile_id: profileId || null,
    });
    if (error) {
      setActionError(`Failed to delete user: ${error.message}`);
      setActionLoading(null);
      return;
    }
    await fetchUsers();
    setConfirmDelete(null);
    setActionLoading(null);
  };

  const deleteTechProfile = async (profileId, userId) => {
    setActionLoading(profileId);
    setActionError(null);
    if (!(await purgeProfileFiles(profileId, userId))) return;
    const { error } = await adminRpc('admin_delete_user', {
      target_user_id: userId || null, target_profile_id: profileId,
    });
    if (error) {
      setActionError(`Failed to delete tech profile: ${error.message}`);
      setActionLoading(null);
      return;
    }
    await fetchTechs();
    setConfirmDelete(null);
    setActionLoading(null);
  };

  // The three edits below go through admin_update_tech_profile. Direct UPDATEs
  // were blocked by the auth.uid() = user_id policy (migration 002) -- zero rows
  // affected, no error -- while the optimistic setTechs below made the change
  // look like it had stuck until the next refetch. Only patch local state once
  // the RPC has actually reported success.
  const toggleOpenToWork = async (profileId, currentValue) => {
    setActionLoading(profileId);
    setActionError(null);
    const { error } = await adminRpc('admin_update_tech_profile', {
      target_profile_id: profileId, new_open_to_work: !currentValue,
    });
    if (error) {
      setActionError(`Failed to update open-to-work: ${error.message}`);
    } else {
      setTechs(prev => prev.map(t => t.id === profileId ? { ...t, open_to_work: !currentValue } : t));
    }
    setActionLoading(null);
  };

  const toggleVisibleToTechs = async (profileId, currentValue) => {
    setActionLoading(profileId);
    setActionError(null);
    const { error } = await adminRpc('admin_update_tech_profile', {
      target_profile_id: profileId, new_visible_to_techs: !currentValue,
    });
    if (error) {
      setActionError(`Failed to update visibility: ${error.message}`);
    } else {
      setTechs(prev => prev.map(t => t.id === profileId ? { ...t, visible_to_techs: !currentValue } : t));
    }
    setActionLoading(null);
  };

  const saveFieldHours = async (profileId, value) => {
    const hours = parseInt(value, 10);
    if (isNaN(hours) || hours < 0) { setEditingHours(null); return; }
    setActionLoading(profileId);
    setActionError(null);
    const { error } = await adminRpc('admin_update_tech_profile', {
      target_profile_id: profileId, new_total_field_hours: hours,
    });
    if (error) {
      setActionError(`Failed to update field hours: ${error.message}`);
    } else {
      setTechs(prev => prev.map(t => t.id === profileId ? { ...t, total_field_hours: hours } : t));
    }
    setEditingHours(null);
    setActionLoading(null);
  };

  const deleteEmployer = async (profileId, userId) => {
    setActionLoading(profileId);
    setActionError(null);
    // Employer accounts own no uxo-uploads files, but deleting one by user_id
    // also removes any tech profile on that same account -- so the same purge
    // has to run first.
    if (!(await purgeProfileFiles(null, userId))) return;
    const { error } = await adminRpc('admin_delete_user', {
      target_user_id: userId || null, target_profile_id: profileId,
    });
    if (error) {
      setActionError(`Failed to delete employer: ${error.message}`);
      setActionLoading(null);
      return;
    }
    await fetchEmployers();
    setConfirmDelete(null);
    setActionLoading(null);
  };

  // Same story as the tech_profiles edits: user_accounts UPDATE is own-row only
  // (migration 025), so the direct write silently did nothing -- including when
  // granting is_paid_employer, which is what lets an employer bypass Stripe.
  const togglePaidEmployer = async (userId, currentValue) => {
    setActionLoading(userId);
    setActionError(null);
    const { error } = await adminRpc('admin_update_user_account', {
      target_user_id: userId, new_is_paid_employer: !currentValue,
    });
    if (error) {
      setActionError(`Failed to update paid status: ${error.message}`);
    } else {
      setEmployers(prev => prev.map(e => e.user_id === userId ? { ...e, is_paid_employer: !currentValue } : e));
    }
    setActionLoading(null);
  };

  const saveUnlockBalance = async (employerId, value) => {
    const bal = parseInt(value, 10);
    if (isNaN(bal) || bal < 0) { setEditingBalance(null); return; }
    setActionLoading(employerId);
    setActionError(null);
    // The result was previously discarded, so a failed write still repainted
    // the new balance and looked like it had saved until the next refetch.
    // Unlock balance is what an employer spends on contact reveals -- a silent
    // no-op here is a support ticket about a grant that never arrived.
    const { error } = await adminRpc('admin_update_unlock_balance', {
      target_employer_id: employerId,
      new_balance: bal,
    });
    if (error) {
      setActionError(`Failed to update unlock balance: ${error.message}`);
    } else {
      setEmployers(prev => prev.map(e =>
        e.id === employerId ? { ...e, balance: { ...e.balance, current_balance: bal } } : e
      ));
    }
    setEditingBalance(null);
    setActionLoading(null);
  };

  const deleteApplication = async (appId) => {
    setActionLoading(appId);
    setActionError(null);
    const { error } = await adminRpc('admin_delete_application', {
      target_application_id: appId,
    });
    if (error) {
      setActionError(`Failed to delete application: ${error.message}`);
      setActionLoading(null);
      return;
    }
    await fetchApplications();
    setConfirmDelete(null);
    setActionLoading(null);
  };

  const deleteJobPost = async (postId) => {
    setActionLoading(postId);
    setActionError(null);
    const { error } = await adminRpc('admin_delete_job_post', {
      target_post_id: postId,
    });
    if (error) {
      setActionError(`Failed to delete job post: ${error.message}`);
      setActionLoading(null);
      return;
    }
    await fetchJobPosts();
    setConfirmDelete(null);
    setActionLoading(null);
  };

  // ── User controls ─────────────────────────────────────────────────────────
  // flagged lives on user_accounts, so this is only callable for rows that have
  // an account -- the Flag button is disabled on accountless profile rows.
  const toggleFlagged = async (userId, currentValue) => {
    if (!userId) return;
    setActionLoading(userId);
    setActionError(null);
    const { error } = await adminRpc('admin_update_user_account', {
      target_user_id: userId, new_flagged: !currentValue,
    });
    if (error) {
      setActionError(`Failed to update flag: ${error.message}`);
    } else {
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, flagged: !currentValue } : u));
    }
    setActionLoading(null);
  };

  const sendDirectMessage = async () => {
    if (!messagingUser || !messageText.trim()) return;
    setSendingMessage(true);
    try {
      await supabase.functions.invoke('send-email', {
        body: {
          to: messagingUser.email,
          from: 'UXOHire Support <support@uxohire.com>',
          subject: 'Message from UXOHire Support',
          html: `
            <div style="font-family:Georgia,serif;background:#0d0f10;color:#e8e4dc;padding:32px;border-radius:10px;">
              <h2 style="color:#d97706;margin:0 0 16px;">Message from UXOHire Support</h2>
              <div style="background:#111316;border:1px solid #2a2c2e;border-radius:8px;padding:20px;line-height:1.7;">
                ${messageText.trim().replace(/\n/g, '<br/>')}
              </div>
              <p style="color:#7a7570;font-size:12px;margin-top:20px;">
                This message was sent by UXOHire administration. Do not reply to this email.
              </p>
            </div>
          `,
        },
      });
      setBulkSuccess(`Message sent to ${messagingUser.name || messagingUser.email}`);
      setTimeout(() => setBulkSuccess(null), 4000);
    } catch (err) {
      setActionError(`Failed to send message: ${err.message}`);
    }
    setMessagingUser(null);
    setMessageText('');
    setSendingMessage(false);
  };

  // ── Platform controls ─────────────────────────────────────────────────────
  const toggleMaintenance = () => {
    const newVal = !maintenanceMode;
    setMaintenanceMode(newVal);
    if (newVal) {
      localStorage.setItem(MAINTENANCE_KEY, 'true');
    } else {
      localStorage.removeItem(MAINTENANCE_KEY);
    }
  };

  const sendBroadcast = async (targetType) => {
    if (!broadcastText.trim()) return;
    setBroadcastSending(true);
    setBroadcastConfirm(null);

    try {
      // Fetch emails via RPC (bypasses RLS)
      const { data: emails, error: rpcErr } = await adminRpc('admin_list_emails', {
        target_type: targetType,
      });

      if (rpcErr) {
        setActionError(`Failed to fetch emails: ${rpcErr.message}`);
        setBroadcastSending(false);
        return;
      }

      const validEmails = (emails || []).filter(Boolean);
      if (validEmails.length === 0) {
        setActionError(`No ${targetType} email addresses found.`);
        setBroadcastSending(false);
        return;
      }

      const htmlBody = `
        <div style="font-family:Georgia,serif;background:#0d0f10;color:#e8e4dc;padding:32px;border-radius:10px;">
          <h2 style="color:#d97706;margin:0 0 16px;">UXOHire Announcement</h2>
          <div style="background:#111316;border:1px solid #2a2c2e;border-radius:8px;padding:20px;line-height:1.7;">
            ${broadcastText.trim().replace(/\n/g, '<br/>')}
          </div>
          <p style="color:#7a7570;font-size:12px;margin-top:20px;">
            You received this message because you have an account on UXOHire.
          </p>
        </div>
      `;

      // Send one email per recipient so addresses stay private
      let sent = 0;
      let failed = 0;
      for (const email of validEmails) {
        const { data: result } = await supabase.functions.invoke('send-email', {
          body: {
            to: email,
            from: 'UXOHire <noreply@uxohire.com>',
            subject: 'Message from UXOHire',
            html: htmlBody,
          },
        });
        if (result?.error) { failed++; } else { sent++; }
      }

      if (failed > 0) {
        setBulkSuccess(`Broadcast sent to ${sent} ${targetType} (${failed} failed)`);
      } else {
        setBulkSuccess(`Broadcast sent to ${sent} ${targetType}`);
      }
      setTimeout(() => setBulkSuccess(null), 5000);
      setBroadcastText('');
    } catch (err) {
      setActionError(`Broadcast failed: ${err.message}`);
    }
    setBroadcastSending(false);
  };

  const exportTechCSV = async () => {
    setExportingTechs(true);
    setActionError(null);
    // Same RPC as the Tech Profiles tab. Reading the table directly exported
    // only open-to-work techs, which also made the "Open to Work" column below
    // incapable of ever printing "No".
    const { data, error } = await adminRpc('admin_list_tech_profiles');
    if (error) {
      setActionError(`Failed to export techs: ${error.message}`);
      setExportingTechs(false);
      return;
    }
    const rows = (data || []).map(t => [
      (t.name || '').replace(/,/g, ' '),
      (t.email || '').replace(/,/g, ' '),
      (t.location || '').replace(/,/g, ' '),
      t.total_field_hours || t.uxo_hours || '',
      (t.dod_certs || []).join('; '),
      t.open_to_work ? 'Yes' : 'No',
    ]);
    const csv = 'Name,Email,Location,Hours,Certs,Open to Work\n' + rows.map(r => r.join(',')).join('\n');
    downloadCSV(csv, 'uxohire_techs.csv');
    setExportingTechs(false);
  };

  const exportEmployerCSV = async () => {
    setExportingEmployers(true);
    setActionError(null);
    // admin_list_employers already returns is_paid_employer and unlock_count.
    // The old version joined those client-side from user_accounts and
    // contact_unlocks, both own-row only from the browser, so every employer
    // exported as Free with 0 unlocks.
    const { data: empData, error } = await adminRpc('admin_list_employers');
    if (error) {
      setActionError(`Failed to export employers: ${error.message}`);
      setExportingEmployers(false);
      return;
    }

    const rows = (empData || []).map(e => [
      (e.company_name || '').replace(/,/g, ' '),
      (e.contact_email || '').replace(/,/g, ' '),
      e.is_paid_employer ? 'Paid' : 'Free',
      e.unlock_count || 0,
    ]);
    const csv = 'Company Name,Contact Email,Paid Status,Unlock Count\n' + rows.map(r => r.join(',')).join('\n');
    downloadCSV(csv, 'uxohire_employers.csv');
    setExportingEmployers(false);
  };

  function downloadCSV(csv, filename) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Bulk selection helpers ──────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids) => {
    setSelected(prev => {
      const allSelected = ids.every(id => prev.has(id));
      if (allSelected) return new Set();
      return new Set(ids);
    });
  };

  // Deleting users in bulk purges storage first, one item at a time, for the
  // same reason the single-delete paths do: admin_delete_user cascades
  // tech_documents away and the file paths become unrecoverable. This loop used
  // to skip the purge entirely, so bulk-deleting techs orphaned every one of
  // their uploads -- the same failure the 2026-08-02 sweep found 42 of.
  //
  // A purge failure skips that item's delete rather than aborting the whole
  // run: the other selected rows are independent, and stopping halfway would
  // leave the admin guessing which ones went. The item is counted as failed and
  // called out separately, because "we could not delete this" and "we deleted
  // the account but left its files" are different outcomes and only the first
  // leaves the row on screen.
  const bulkDelete = async (items) => {
    setBulkDeleting(true);
    setBulkProgress({ done: 0, total: items.length });
    setBulkSuccess(null);
    setActionError(null);
    let failed = 0;
    let fileBlocked = 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      let error;
      if (item.type === 'application') {
        ({ error } = await adminRpc('admin_delete_application', {
          target_application_id: item.id,
        }));
      } else if (item.type === 'job') {
        ({ error } = await adminRpc('admin_delete_job_post', {
          target_post_id: item.id,
        }));
      } else {
        // isEmployer rows carry an employer_profiles id, not a tech one.
        // admin_collect_profile_files treats target_profile_id as authoritative
        // without checking which table it came from, so handing it an employer
        // id matches no tech_documents row, returns no paths and reports
        // success -- a silent skip. Passing null lets it resolve the tech
        // profile from user_id instead, which is what deleteEmployer does and
        // why deleting an employer can still take a tech profile's files with
        // it when both sit on one account.
        const purge = await adminDeleteProfileFiles(
          item.isEmployer ? null : (item.profileId || null),
          item.userId || null
        );
        if (!purge.ok) {
          failed++;
          fileBlocked++;
          setBulkProgress({ done: i + 1, total: items.length });
          continue;
        }
        ({ error } = await adminRpc('admin_delete_user', {
          target_user_id: item.userId || null,
          target_profile_id: item.profileId || null,
        }));
      }
      if (error) failed++;
      setBulkProgress({ done: i + 1, total: items.length });
    }

    setSelected(new Set());
    setConfirmDelete(null);
    setBulkDeleting(false);

    if (failed > 0) {
      setActionError(
        fileBlocked > 0
          ? `${failed} of ${items.length} deletions failed — ${fileBlocked} because the profile's files could not be deleted, so those accounts were left intact.`
          : `${failed} of ${items.length} deletions failed.`
      );
    }
    const succeeded = items.length - failed;
    if (succeeded > 0) {
      setBulkSuccess(`${succeeded} item${succeeded !== 1 ? 's' : ''} deleted successfully`);
      setTimeout(() => setBulkSuccess(null), 4000);
    }

    if (activeTab === 'users') await fetchUsers();
    else if (activeTab === 'techs') await fetchTechs();
    else if (activeTab === 'employers') await fetchEmployers();
    else if (activeTab === 'jobs') await fetchJobPosts();
    else if (activeTab === 'applications') await fetchApplications();
  };

  // Clear selection when switching tabs
  useEffect(() => { setSelected(new Set()); }, [activeTab]);

  if (authed === null) {
    return (
      <div style={s.root}>
        <div style={s.loginWrap}>
          <div style={{ color: '#7a7570', fontSize: 15 }}>Checking admin access…</div>
        </div>
      </div>
    );
  }

  if (!authed) {
    return (
      <AdminLogin
        notice={gateNotice}
        onLogin={(email) => {
          setGateNotice('');
          setAdminEmail(email);
          setLoading(true);
          setAuthed(true);
        }}
      />
    );
  }

  // ── Filters ────────────────────────────────────────────────────────────────
  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchTerm ||
      (u.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = accountFilter === 'all' || u.account_type === accountFilter;
    return matchesSearch && matchesFilter;
  });

  const filteredTechs = techs.filter(t =>
    !searchTerm ||
    (t.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.location || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEmployers = employers.filter(e =>
    !searchTerm ||
    (e.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (e.contact_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (e.contact_email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredJobPosts = jobPosts.filter(j => {
    const matchesSearch = !searchTerm ||
      (j.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (j.company || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || j.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredApplications = applications.filter(a => {
    const matchesSearch = !searchTerm ||
      (a.applicant_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.job_company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.job_title || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const verificationPendingCount = logbookPendingCount + qualityPendingCount;

  const tabs = [
    { id: 'users', label: 'Users', icon: '\uD83D\uDC65' },
    { id: 'techs', label: 'Tech Profiles', icon: '\uD83E\uDE96' },
    { id: 'logbook', label: 'Document Review', icon: '\uD83D\uDCD6', badge: verificationPendingCount },
    { id: 'employers', label: 'Employers', icon: '\uD83C\uDFE2' },
    { id: 'jobs', label: 'Job Posts', icon: '\uD83D\uDCCB' },
    { id: 'applications', label: 'Applications', icon: '\uD83D\uDCC4' },
    { id: 'resources', label: 'Resources', icon: '\uD83D\uDCDA' },
    { id: 'platform', label: 'Platform', icon: '\u2699\uFE0F' },
    { id: 'stats', label: 'Stats', icon: '\uD83D\uDCCA' },
  ];

  return (
    <div style={s.root}>
      {/* Confirm dialog */}
      {confirmDelete && (
        <ConfirmDialog
          message={confirmDelete.message}
          onConfirm={confirmDelete.onConfirm}
          onCancel={() => setConfirmDelete(null)}
          confirmLabel={confirmDelete.confirmLabel}
          requireTypedConfirmation={confirmDelete.requireTypedConfirmation}
        />
      )}

      {/* Broadcast confirm dialog */}
      {broadcastConfirm && (
        <ConfirmDialog
          message={`Send this broadcast to ALL ${broadcastConfirm}? This cannot be undone.`}
          onConfirm={() => sendBroadcast(broadcastConfirm)}
          onCancel={() => setBroadcastConfirm(null)}
          confirmLabel="Send Broadcast"
        />
      )}

      {/* Direct message modal */}
      {messagingUser && (
        <div style={s.overlay} onClick={() => { setMessagingUser(null); setMessageText(''); }}>
          <div style={s.dialog} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Message {messagingUser.name}</h3>
              <button style={s.closeBtn} onClick={() => { setMessagingUser(null); setMessageText(''); }}>{"\u2715"}</button>
            </div>
            <div style={{ fontSize: 12, color: '#7a7570', marginBottom: 12 }}>
              To: {messagingUser.email} (from support@uxohire.com)
            </div>
            <textarea
              style={{ ...s.input, minHeight: 120, resize: 'vertical' }}
              placeholder="Type your message..."
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
              <button style={s.btnSecondary} onClick={() => { setMessagingUser(null); setMessageText(''); }}>Cancel</button>
              <button
                style={{ ...s.btnPrimary, opacity: (!messageText.trim() || sendingMessage) ? 0.5 : 1 }}
                disabled={!messageText.trim() || sendingMessage}
                onClick={sendDirectMessage}
              >
                {sendingMessage ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action error banner */}
      {actionError && (
        <div style={s.actionErrorBanner}>
          <span>{actionError}</span>
          <button style={s.closeBtn} onClick={() => setActionError(null)}>{"\u2715"}</button>
        </div>
      )}

      {/* Bulk success banner */}
      {bulkSuccess && (
        <div style={s.successBanner}>
          <span>{bulkSuccess}</span>
          <button style={s.closeBtn} onClick={() => setBulkSuccess(null)}>{"\u2715"}</button>
        </div>
      )}

      {/* Bulk delete progress overlay */}
      {bulkDeleting && (
        <div style={s.overlay}>
          <div style={s.dialog}>
            <p style={{ color: '#e8e4dc', fontSize: 15, margin: '0 0 16px' }}>
              Deleting {bulkProgress.done} of {bulkProgress.total}...
            </p>
            <div style={s.progressTrack}>
              <div style={{ ...s.progressBar, width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Profile detail modal */}
      {viewingProfile && (
        <div style={s.overlay} onClick={() => setViewingProfile(null)}>
          <div style={s.profileModal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 20 }}>{viewingProfile.name || viewingProfile.company_name || 'Profile'}</h3>
              <button style={s.closeBtn} onClick={() => setViewingProfile(null)}>{"\u2715"}</button>
            </div>
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {/* Tech profile detail controls */}
              {viewingProfile.user_id && viewingProfile.dod_certs !== undefined && (
                <>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                    <button
                      style={{
                        ...s.toggleBtnSmall,
                        background: viewingProfile.visible_to_techs !== false ? '#0a2a1a' : '#2a0a0a',
                        color: viewingProfile.visible_to_techs !== false ? '#4ade80' : '#f87171',
                        border: `1px solid ${viewingProfile.visible_to_techs !== false ? '#4ade8033' : '#f8717133'}`,
                      }}
                      onClick={async () => {
                        const cur = viewingProfile.visible_to_techs !== false;
                        await toggleVisibleToTechs(viewingProfile.id, cur);
                        setViewingProfile(p => ({ ...p, visible_to_techs: !cur }));
                      }}
                    >
                      Visible to Techs: {viewingProfile.visible_to_techs !== false ? 'On' : 'Off'}
                    </button>
                    <button
                      style={{
                        ...s.toggleBtnSmall,
                        background: viewingProfile.open_to_work ? '#0a2a1a' : '#2a0a0a',
                        color: viewingProfile.open_to_work ? '#4ade80' : '#f87171',
                        border: `1px solid ${viewingProfile.open_to_work ? '#4ade8033' : '#f8717133'}`,
                      }}
                      onClick={async () => {
                        await toggleOpenToWork(viewingProfile.id, viewingProfile.open_to_work);
                        setViewingProfile(p => ({ ...p, open_to_work: !p.open_to_work }));
                      }}
                    >
                      Open to Work: {viewingProfile.open_to_work ? 'On' : 'Off'}
                    </button>
                  </div>
                  {/* Logbook status */}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, fontSize: 13 }}>
                    <span style={{ color: '#7a7570' }}>Has Logbook:</span>
                    <span style={{ color: viewingProfile.has_logbook ? '#4ade80' : '#7a7570', fontWeight: 'bold' }}>
                      {viewingProfile.has_logbook ? 'Yes' : 'No'}
                    </span>
                    {viewingProfile.has_logbook && !['verified', 'pending', 'returned'].includes(viewingProfile.logbook_verification_status) && (
                      <span style={{ color: '#d97706', fontSize: 11 }}>
                        (Claims logbook but has not uploaded one or not yet submitted)
                      </span>
                    )}
                    <span style={{ color: '#7a7570', marginLeft: 8 }}>Document review:</span>
                    <span style={{
                      color: viewingProfile.logbook_verification_status === 'verified' ? '#4ade80'
                        : viewingProfile.logbook_verification_status === 'pending' ? '#fbbf24'
                        : viewingProfile.logbook_verification_status === 'returned' ? '#fbbf24' : '#7a7570',
                      fontWeight: 'bold',
                    }}>
                      {viewingProfile.logbook_verification_status || 'unverified'}
                    </span>
                    {viewingProfile.logbook_verified_hours != null && (
                      <span style={{ color: '#d97706' }}>
                        ({viewingProfile.logbook_verified_hours.toLocaleString()} hrs documented)
                      </span>
                    )}
                  </div>
                  <AdminDocSection profile={viewingProfile} allowDelete />
                  <AdminTechInvitations techId={viewingProfile.id} />
                </>
              )}

              {/* Employer detail: overage reset + unlocked techs */}
              {viewingProfile.company_name !== undefined && viewingProfile.user_id && (
                <>
                  {(viewingProfile.unlock_overage_count || 0) > 0 && (
                    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 13, color: '#7a7570' }}>
                        Overage count: <strong style={{ color: '#f87171' }}>{viewingProfile.unlock_overage_count}</strong>
                      </span>
                      <button
                        style={{
                          ...s.toggleBtnSmall,
                          background: '#2a0a0a', color: '#f87171',
                          border: '1px solid #f8717133',
                        }}
                        onClick={async () => {
                          // Error was previously discarded: a failed reset still
                          // painted the count to 0 and reported success, and the
                          // overage count is what the employer gets billed on.
                          const { error } = await adminRpc('admin_reset_overage', {
                            target_employer_id: viewingProfile.id,
                          });
                          if (error) {
                            setActionError(`Failed to reset overage: ${error.message}`);
                            return;
                          }
                          setViewingProfile(p => ({ ...p, unlock_overage_count: 0 }));
                          setBulkSuccess('Overage count reset to 0');
                          setTimeout(() => setBulkSuccess(null), 3000);
                        }}
                      >
                        Reset Overage
                      </button>
                    </div>
                  )}
                  <AdminUnlockedTechs employerId={viewingProfile.id} />
                  <AdminEmployerInvitations employerId={viewingProfile.id} />
                </>
              )}

              <pre style={s.profilePre}>{JSON.stringify(viewingProfile, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={s.header}>
        <div style={s.headerInner}>
          <div>
            <div style={s.headerLabel}>Admin Dashboard</div>
            <div style={s.headerName}>UXOHire Administration</div>
            {adminEmail && (
              <div style={{ color: '#7a7570', fontSize: 12, marginTop: 4 }}>
                Signed in as {adminEmail}
              </div>
            )}
          </div>
          {/* Ends the Supabase session itself. The old button only dropped a
              localStorage flag, which the gateway never saw. */}
          <button style={s.logoutBtn} onClick={async () => { await supabase.auth.signOut(); setAuthed(false); }}>
            Log Out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            style={{ ...s.tab, ...(activeTab === tab.id ? s.tabActive : {}) }}
            onClick={() => { setActiveTab(tab.id); setSearchTerm(''); setAccountFilter('all'); setStatusFilter('all'); }}
          >
            <span>{tab.icon}</span> {tab.label}
            {tab.badge > 0 && (
              <span style={{
                background: '#d97706', color: '#0d0f10', fontSize: 10, fontWeight: 'bold',
                borderRadius: 8, padding: '1px 6px', marginLeft: 4,
              }}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      <div style={s.body}>
        {loading && <div style={{ color: '#7a7570', textAlign: 'center', padding: 40 }}>Loading...</div>}

        {/* ═══════════ USER MANAGEMENT TAB ═══════════ */}
        {!loading && activeTab === 'users' && (
          <div>
            <div style={s.toolbar}>
              <input
                style={s.searchInput}
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <select style={s.filterSelect} value={accountFilter} onChange={e => setAccountFilter(e.target.value)}>
                <option value="all">All Types</option>
                <option value="technician">Technicians</option>
                <option value="employer">Employers</option>
              </select>
              {selected.size > 0 && (
                <button
                  style={s.bulkDeleteBtn}
                  onClick={() => setConfirmDelete({
                    message: `Delete ${selected.size} account${selected.size !== 1 ? 's' : ''}? This cannot be undone.`,
                    onConfirm: () => bulkDelete(
                      // profile_id here is COALESCE(tech, employer) — see admin_list_users
                      // in migration 074 — so the account type is the only way to know
                      // which table it points at.
                      filteredUsers.filter(u => selected.has(u.id)).map(u => ({
                        userId: u.user_id,
                        profileId: u.profile_id,
                        isEmployer: u.account_type === 'employer',
                      }))
                    ),
                  })}
                >
                  Delete Selected ({selected.size})
                </button>
              )}
              <span style={s.countBadge}>{filteredUsers.length} users</span>
            </div>

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>
                      <input type="checkbox" style={s.checkbox}
                        checked={filteredUsers.length > 0 && filteredUsers.every(u => selected.has(u.id))}
                        onChange={() => toggleSelectAll(filteredUsers.map(u => u.id))}
                      />
                    </th>
                    <th style={s.th}>Name</th>
                    <th style={s.th}>Email</th>
                    <th style={s.th}>Type</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Created</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.id} style={{ ...s.tr, background: selected.has(u.id) ? '#1a1c2e' : undefined }}>
                      <td style={s.td}>
                        <input type="checkbox" style={s.checkbox}
                          checked={selected.has(u.id)}
                          onChange={() => toggleSelect(u.id)}
                        />
                      </td>
                      <td style={s.td}>{u.name}</td>
                      <td style={s.td}><span style={s.emailText}>{u.email || '\u2014'}</span></td>
                      <td style={s.td}>
                        <span style={{
                          ...s.typeBadge,
                          background: u.account_type === 'technician' ? '#0a2a1a' : '#0a1a2a',
                          color: u.account_type === 'technician' ? '#4ade80' : '#60a5fa',
                          border: `1px solid ${u.account_type === 'technician' ? '#4ade8033' : '#60a5fa33'}`,
                        }}>
                          {u.account_type}
                        </span>
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {u.flagged && (
                            <span style={{
                              ...s.typeBadge,
                              background: '#2a0a0a', color: '#f87171',
                              border: '1px solid #f8717133',
                            }}>
                              Flagged
                            </span>
                          )}
                          {/* Profile rows with no user_accounts record: legacy or
                              anonymous submissions that can't log in. They were
                              absent from this list entirely until migration 074. */}
                          {!u.has_account && (
                            <span
                              style={{
                                ...s.typeBadge,
                                background: '#1a1c1e', color: '#7a7570',
                                border: '1px solid #2a2c2e',
                              }}
                              title="Profile with no login account — cannot sign in, and cannot be flagged"
                            >
                              No account
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={s.td}>{new Date(u.created_at).toLocaleDateString()}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            style={{
                              ...s.toggleBtnSmall,
                              background: u.flagged ? '#2a0a0a' : '#1a1c1e',
                              color: u.flagged ? '#f87171' : '#7a7570',
                              border: `1px solid ${u.flagged ? '#f8717133' : '#2a2c2e'}`,
                              opacity: u.has_account ? 1 : 0.4,
                              cursor: u.has_account ? 'pointer' : 'not-allowed',
                            }}
                            disabled={!u.has_account || actionLoading === u.user_id}
                            onClick={() => toggleFlagged(u.user_id, u.flagged)}
                            title={u.has_account
                              ? (u.flagged ? 'Unflag account' : 'Flag account')
                              : 'No account to flag'}
                          >
                            {u.flagged ? 'Unflag' : 'Flag'}
                          </button>
                          {u.email && (
                            <button
                              style={s.viewBtnSmall}
                              onClick={() => setMessagingUser({ userId: u.user_id, email: u.email, name: u.name })}
                              title="Send email"
                            >
                              Message
                            </button>
                          )}
                          <button
                            style={s.deleteBtnSmall}
                            disabled={actionLoading === (u.user_id || u.profile_id)}
                            onClick={() => setConfirmDelete(u.is_paid_employer ? {
                              message: `This is a paid employer account. Deleting will permanently remove all their job posts, applications, messages, and unlock history.`,
                              onConfirm: () => deleteUser(u.user_id, u.profile_id),
                              requireTypedConfirmation: u.email || u.name,
                            } : {
                              message: u.has_account
                                ? `Delete ${u.name}? This will permanently remove their account and all associated data.`
                                : `Delete ${u.name}? This profile has no login account; deleting removes the profile and all associated data.`,
                              onConfirm: () => deleteUser(u.user_id, u.profile_id),
                            })}
                          >
                            {actionLoading === (u.user_id || u.profile_id) ? '\u2026' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length === 0 && (
                <div style={s.emptyTable}>No users found.</div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ TECH PROFILES TAB ═══════════ */}
        {!loading && activeTab === 'techs' && (
          <div>
            <div style={s.toolbar}>
              <input
                style={s.searchInput}
                placeholder="Search by name or location..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {selected.size > 0 && (
                <button
                  style={s.bulkDeleteBtn}
                  onClick={() => setConfirmDelete({
                    message: `Delete ${selected.size} account${selected.size !== 1 ? 's' : ''}? This cannot be undone.`,
                    onConfirm: () => bulkDelete(
                      filteredTechs.filter(t => selected.has(t.id)).map(t => ({ userId: t.user_id, profileId: t.id }))
                    ),
                  })}
                >
                  Delete Selected ({selected.size})
                </button>
              )}
              <span style={s.countBadge}>{filteredTechs.length} profiles</span>
            </div>

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>
                      <input type="checkbox" style={s.checkbox}
                        checked={filteredTechs.length > 0 && filteredTechs.every(t => selected.has(t.id))}
                        onChange={() => toggleSelectAll(filteredTechs.map(t => t.id))}
                      />
                    </th>
                    <th style={s.th}>Name</th>
                    <th style={s.th}>Location</th>
                    <th style={s.th}>Certs</th>
                    <th style={s.th}>Hours</th>
                    <th style={s.th}>Visible</th>
                    <th style={s.th}>Open to Work</th>
                    <th style={s.th}>Updated</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTechs.map(t => (
                    <tr key={t.id} style={{ ...s.tr, background: selected.has(t.id) ? '#1a1c2e' : undefined }}>
                      <td style={s.td}>
                        <input type="checkbox" style={s.checkbox}
                          checked={selected.has(t.id)}
                          onChange={() => toggleSelect(t.id)}
                        />
                      </td>
                      <td style={s.td}>
                        <button style={s.linkBtn} onClick={() => setViewingProfile(t)}>{t.name}</button>
                      </td>
                      <td style={s.td}>{t.location || '\u2014'}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {(t.dod_certs || []).slice(0, 2).map(c => (
                            <span key={c} style={s.miniCert}>{c}</span>
                          ))}
                          {(t.dod_certs || []).length > 2 && (
                            <span style={s.miniCert}>+{(t.dod_certs || []).length - 2}</span>
                          )}
                          {(t.dod_certs || []).length === 0 && '\u2014'}
                        </div>
                      </td>
                      <td style={s.td}>
                        {editingHours?.id === t.id ? (
                          <input
                            type="number"
                            min="0"
                            style={{ ...s.input, width: 80, padding: '4px 8px', fontSize: 13 }}
                            value={editingHours.value}
                            onChange={e => setEditingHours({ id: t.id, value: e.target.value })}
                            onBlur={() => saveFieldHours(t.id, editingHours.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveFieldHours(t.id, editingHours.value); if (e.key === 'Escape') setEditingHours(null); }}
                            autoFocus
                          />
                        ) : (
                          <span
                            style={{ cursor: 'pointer', borderBottom: '1px dashed #555' }}
                            onClick={() => setEditingHours({ id: t.id, value: t.total_field_hours || t.uxo_hours || '' })}
                            title="Click to edit"
                          >
                            {t.total_field_hours || t.uxo_hours || '\u2014'}
                          </span>
                        )}
                      </td>
                      <td style={s.td}>
                        <button
                          style={{
                            ...s.toggleBtnSmall,
                            background: t.visible_to_techs !== false ? '#0a2a1a' : '#2a0a0a',
                            color: t.visible_to_techs !== false ? '#4ade80' : '#f87171',
                            border: `1px solid ${t.visible_to_techs !== false ? '#4ade8033' : '#f8717133'}`,
                          }}
                          disabled={actionLoading === t.id}
                          onClick={() => toggleVisibleToTechs(t.id, t.visible_to_techs !== false)}
                        >
                          {t.visible_to_techs !== false ? 'Yes' : 'No'}
                        </button>
                      </td>
                      <td style={s.td}>
                        <button
                          style={{
                            ...s.toggleBtnSmall,
                            background: t.open_to_work ? '#0a2a1a' : '#2a0a0a',
                            color: t.open_to_work ? '#4ade80' : '#f87171',
                            border: `1px solid ${t.open_to_work ? '#4ade8033' : '#f8717133'}`,
                          }}
                          disabled={actionLoading === t.id}
                          onClick={() => toggleOpenToWork(t.id, t.open_to_work)}
                        >
                          {t.open_to_work ? 'Yes' : 'No'}
                        </button>
                      </td>
                      <td style={s.td}>{t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '\u2014'}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button style={s.viewBtnSmall} onClick={() => setViewingProfile(t)}>View</button>
                          <button
                            style={s.deleteBtnSmall}
                            disabled={actionLoading === t.id}
                            onClick={() => setConfirmDelete({
                              message: `Delete ${t.name}'s tech profile? This will permanently remove their profile and all associated data.`,
                              onConfirm: () => deleteTechProfile(t.id, t.user_id),
                            })}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredTechs.length === 0 && (
                <div style={s.emptyTable}>No tech profiles found.</div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ LOGBOOK VERIFICATION QUEUE ═══════════ */}
        {!loading && activeTab === 'logbook' && (
          <div>
            <div style={s.toolbar}>
              <span style={s.countBadge}>{logbookQueue.length} pending logbook submissions</span>
            </div>

            {logbookQueueLoading && <div style={{ color: '#7a7570', textAlign: 'center', padding: 40 }}>Loading queue...</div>}

            {!logbookQueueLoading && logbookQueue.length === 0 && (
              <div style={{ background: '#111316', border: '1px solid #1e2022', borderRadius: 10, padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{"\u2705"}</div>
                <p style={{ color: '#e8e4dc', fontSize: 15, margin: '0 0 8px' }}>All clear!</p>
                <p style={{ color: '#7a7570', fontSize: 13, margin: 0 }}>No logbook submissions pending review.</p>
              </div>
            )}

            {!logbookQueueLoading && logbookQueue.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {logbookQueue.map(tech => {
                  const doc = tech.logbookDoc;
                  const isProcessing = verifyingTech === tech.id;
                  // The note is what tells the tech what to fix, so it gates the
                  // Return for Correction action.
                  const hasNote = !!(verifyNotesInput[tech.id] || '').trim();

                  // Urgency calculation (business days since upload)
                  let urgencyColor = '#4ade80';
                  let urgencyBg = '#0a2a1a';
                  let urgencyLabel = 'New';
                  let businessDays = 0;
                  if (doc?.uploaded_at) {
                    const uploaded = new Date(doc.uploaded_at);
                    const now = new Date();
                    let count = 0;
                    const d = new Date(uploaded);
                    while (d < now) {
                      d.setDate(d.getDate() + 1);
                      const dow = d.getDay();
                      if (dow !== 0 && dow !== 6) count++;
                    }
                    businessDays = count;
                    if (businessDays >= 5) {
                      urgencyColor = '#f87171'; urgencyBg = '#2a0a0a'; urgencyLabel = 'Overdue';
                    } else if (businessDays >= 3) {
                      urgencyColor = '#fbbf24'; urgencyBg = '#2a1e08'; urgencyLabel = 'Review soon';
                    }
                  }

                  // Check if re-verification needed (self-reported > verified at a threshold)
                  const THRESHOLDS = [5640, 15040];
                  const selfHrs = tech.total_field_hours || 0;
                  const verifiedHrs = tech.logbook_verified_hours || 0;
                  const needsReverification = THRESHOLDS.some(t => selfHrs >= t && verifiedHrs < t);

                  return (
                    <div key={tech.id} style={{
                      background: '#111316', border: `1px solid ${urgencyColor}33`, borderRadius: 10,
                      padding: '20px 22px',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                        <div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 16, fontWeight: 'bold', color: '#e8e4dc' }}>{tech.name}</span>
                            <span style={{
                              background: urgencyBg, color: urgencyColor, fontSize: 11,
                              padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                              border: `1px solid ${urgencyColor}33`,
                            }}>{urgencyLabel}</span>
                            {needsReverification && (
                              <span style={{
                                background: '#2a0a0a', color: '#f87171', fontSize: 11,
                                padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                                border: '1px solid #f8717133',
                              }}>Re-review needed</span>
                            )}
                          </div>
                          <div style={{ fontSize: 13, color: '#7a7570' }}>
                            Self-reported: <strong style={{ color: '#e8e4dc' }}>{selfHrs.toLocaleString()} hrs</strong>
                            {verifiedHrs > 0 && (
                              <span> {"\u00B7"} Previously documented: <strong style={{ color: '#d97706' }}>{verifiedHrs.toLocaleString()} hrs</strong></span>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: '#7a7570', textAlign: 'right' }}>
                          <div>Day {businessDays} of 5</div>
                          {doc && <div>Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}</div>}
                        </div>
                      </div>

                      {/* Logbook view */}
                      {doc && (
                        <div style={{ marginBottom: 12 }}>
                          <button style={{
                            background: 'none', border: '1px solid #60a5fa44', color: '#60a5fa',
                            padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                            fontFamily: 'inherit',
                          }} onClick={async () => {
                            setActionError(null);
                            const { error } = await openAdminDocument(doc.file_path);
                            if (error) setActionError(error);
                          }}>
                            {"\uD83D\uDCC4"} View Logbook: {doc.file_name}
                          </button>
                        </div>
                      )}

                      {!doc && (
                        <div style={{ fontSize: 13, color: '#f87171', marginBottom: 12 }}>
                          No logbook document found for this tech.
                        </div>
                      )}

                      {/* Review form */}
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                          <label style={{ fontSize: 11, color: '#7a7570', display: 'block', marginBottom: 4 }}>Documented MEC Hours</label>
                          <input
                            type="number" min="0"
                            style={{ ...s.input, width: 120, padding: '6px 10px', fontSize: 13 }}
                            placeholder="MEC Hours"
                            value={verifyHoursInput[tech.id] || ''}
                            onChange={e => setVerifyHoursInput(prev => ({ ...prev, [tech.id]: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#7a7570', display: 'block', marginBottom: 4 }}>Documented Supervisory Hours</label>
                          <input
                            type="number" min="0"
                            style={{ ...s.input, width: 150, padding: '6px 10px', fontSize: 13 }}
                            placeholder="Supervisory Hours"
                            value={verifySupervisoryHoursInput[tech.id] || ''}
                            onChange={e => setVerifySupervisoryHoursInput(prev => ({ ...prev, [tech.id]: e.target.value }))}
                          />
                          <div style={{ fontSize: 10, color: '#7a7570', marginTop: 2, maxWidth: 150, lineHeight: 1.3 }}>
                            Hours in supervisory UXO position (UXO-TIII, UXOQCS, UXOSO, or OESS) as shown in logbook
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 150 }}>
                          <label style={{ fontSize: 11, color: '#7a7570', display: 'block', marginBottom: 4 }}>
                            Notes {hasNote ? '' : '(required to return for correction)'}
                          </label>
                          <input
                            type="text"
                            style={{ ...s.input, width: '100%', padding: '6px 10px', fontSize: 13 }}
                            placeholder="Admin notes..."
                            value={verifyNotesInput[tech.id] || ''}
                            onChange={e => setVerifyNotesInput(prev => ({ ...prev, [tech.id]: e.target.value }))}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <button
                            style={{
                              background: '#4ade80', border: 'none', color: '#0d0f10', padding: '7px 16px',
                              borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
                              fontFamily: 'inherit', whiteSpace: 'nowrap',
                              opacity: isProcessing || !verifyHoursInput[tech.id] || (verifySupervisoryHoursInput[tech.id] === undefined || verifySupervisoryHoursInput[tech.id] === null || verifySupervisoryHoursInput[tech.id] === '') ? 0.5 : 1,
                            }}
                            disabled={isProcessing || !verifyHoursInput[tech.id] || (verifySupervisoryHoursInput[tech.id] === undefined || verifySupervisoryHoursInput[tech.id] === null || verifySupervisoryHoursInput[tech.id] === '')}
                            onClick={() => handleVerifyLogbook(tech.id)}
                          >
                            {isProcessing ? 'Processing...' : '\u2713 Mark Documented'}
                          </button>
                          {verifyHoursInput[tech.id] && (verifySupervisoryHoursInput[tech.id] === undefined || verifySupervisoryHoursInput[tech.id] === null || verifySupervisoryHoursInput[tech.id] === '') && (
                            <span style={{ fontSize: 10, color: '#fbbf24', whiteSpace: 'nowrap' }}>Enter documented supervisory hours (enter 0 if none)</span>
                          )}
                        </div>
                        {/* Return for Correction — documents are fine but something
                            needs fixing. Separate from Reject, which means the
                            documents themselves are inadequate. */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <button
                            style={{
                              background: 'none', border: '1px solid #fbbf2444', color: '#fbbf24',
                              padding: '7px 16px', borderRadius: 6,
                              cursor: hasNote && !isProcessing ? 'pointer' : 'not-allowed',
                              fontSize: 13, fontFamily: 'inherit', whiteSpace: 'nowrap',
                              opacity: isProcessing || !hasNote ? 0.5 : 1,
                            }}
                            disabled={isProcessing || !hasNote}
                            onClick={() => handleReturnLogbook(tech.id)}
                          >
                            {isProcessing ? 'Processing...' : '↩ Return for Correction'}
                          </button>
                          {!hasNote && (
                            <span style={{ fontSize: 10, color: '#7a7570', whiteSpace: 'nowrap' }}>Add a note to enable</span>
                          )}
                        </div>
                        <button
                          style={{
                            background: 'none', border: '1px solid #f8717133', color: '#f87171',
                            padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                            fontFamily: 'inherit', whiteSpace: 'nowrap',
                            opacity: isProcessing ? 0.5 : 1,
                          }}
                          disabled={isProcessing}
                          onClick={() => handleRejectLogbook(tech.id)}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══════════ QUALITY CREDENTIALS QUEUE ═══════════ */}
        {!loading && activeTab === 'logbook' && (
          <div style={{ marginTop: 32 }}>
            <div style={s.toolbar}>
              <span style={s.countBadge}>{qualityQueue.length} pending quality credentials</span>
            </div>

            {qualityQueueLoading && <div style={{ color: '#7a7570', textAlign: 'center', padding: 40 }}>Loading queue...</div>}

            {!qualityQueueLoading && qualityQueue.length === 0 && (
              <div style={{ background: '#111316', border: '1px solid #1e2022', borderRadius: 10, padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{"\u2705"}</div>
                <p style={{ color: '#e8e4dc', fontSize: 15, margin: '0 0 8px' }}>All clear!</p>
                <p style={{ color: '#7a7570', fontSize: 13, margin: 0 }}>No quality credential submissions pending review.</p>
              </div>
            )}

            {!qualityQueueLoading && qualityQueue.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {qualityQueue.map(tech => {
                  const doc = tech.qualityDoc;
                  const isProcessing = verifyingTech === tech.id;

                  // Urgency calculation (business days since upload)
                  let urgencyColor = '#4ade80';
                  let urgencyBg = '#0a2a1a';
                  let urgencyLabel = 'New';
                  let businessDays = 0;
                  if (doc?.uploaded_at) {
                    const uploaded = new Date(doc.uploaded_at);
                    const now = new Date();
                    let count = 0;
                    const d = new Date(uploaded);
                    while (d < now) {
                      d.setDate(d.getDate() + 1);
                      const dow = d.getDay();
                      if (dow !== 0 && dow !== 6) count++;
                    }
                    businessDays = count;
                    if (businessDays >= 5) {
                      urgencyColor = '#f87171'; urgencyBg = '#2a0a0a'; urgencyLabel = 'Overdue';
                    } else if (businessDays >= 3) {
                      urgencyColor = '#fbbf24'; urgencyBg = '#2a1e08'; urgencyLabel = 'Review soon';
                    }
                  }

                  return (
                    <div key={tech.id} style={{
                      background: '#111316', border: `1px solid ${urgencyColor}33`, borderRadius: 10,
                      padding: '20px 22px',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                        <div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 16, fontWeight: 'bold', color: '#e8e4dc' }}>{tech.name}</span>
                            <span style={{
                              background: urgencyBg, color: urgencyColor, fontSize: 11,
                              padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                              border: `1px solid ${urgencyColor}33`,
                            }}>{urgencyLabel}</span>
                          </div>
                          <div style={{ fontSize: 13, color: '#7a7570' }}>
                            Credential type: <strong style={{ color: '#e8e4dc' }}>{tech.quality_credential || 'Not specified'}</strong>
                            {' \u00B7 '}Self-reported hours: <strong style={{ color: '#e8e4dc' }}>{(tech.total_field_hours || 0).toLocaleString()}</strong>
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: '#7a7570', textAlign: 'right' }}>
                          <div>Day {businessDays} of 5</div>
                          {doc && <div>Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}</div>}
                        </div>
                      </div>

                      {/* Certificate view */}
                      {doc && (
                        <div style={{ marginBottom: 12 }}>
                          <button style={{
                            background: 'none', border: '1px solid #60a5fa44', color: '#60a5fa',
                            padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                            fontFamily: 'inherit',
                          }} onClick={async () => {
                            setActionError(null);
                            const { error } = await openAdminDocument(doc.file_path);
                            if (error) setActionError(error);
                          }}>
                            {"\uD83D\uDCC4"} View Certificate: {doc.file_name}
                          </button>
                        </div>
                      )}

                      {!doc && (
                        <div style={{ fontSize: 13, color: '#f87171', marginBottom: 12 }}>
                          No quality credential document found for this tech.
                        </div>
                      )}

                      {/* Review form */}
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 150 }}>
                          <label style={{ fontSize: 11, color: '#7a7570', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
                          <input
                            type="text"
                            style={{ ...s.input, width: '100%', padding: '6px 10px', fontSize: 13 }}
                            placeholder="Admin notes..."
                            value={qualityNotesInput[tech.id] || ''}
                            onChange={e => setQualityNotesInput(prev => ({ ...prev, [tech.id]: e.target.value }))}
                          />
                        </div>
                        <button
                          style={{
                            background: '#4ade80', border: 'none', color: '#0d0f10', padding: '7px 16px',
                            borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
                            fontFamily: 'inherit', whiteSpace: 'nowrap',
                            opacity: isProcessing ? 0.5 : 1,
                          }}
                          disabled={isProcessing}
                          onClick={() => handleVerifyQuality(tech.id)}
                        >
                          {isProcessing ? 'Processing...' : '\u2713 Mark Documented'}
                        </button>
                        <button
                          style={{
                            background: 'none', border: '1px solid #f8717133', color: '#f87171',
                            padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                            fontFamily: 'inherit', whiteSpace: 'nowrap',
                            opacity: isProcessing ? 0.5 : 1,
                          }}
                          disabled={isProcessing}
                          onClick={() => handleRejectQuality(tech.id)}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══════════ EMPLOYER PROFILES TAB ═══════════ */}
        {!loading && activeTab === 'employers' && (
          <div>
            <div style={s.toolbar}>
              <input
                style={s.searchInput}
                placeholder="Search by company, contact, or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {selected.size > 0 && (
                <button
                  style={s.bulkDeleteBtn}
                  onClick={() => setConfirmDelete({
                    message: `Delete ${selected.size} account${selected.size !== 1 ? 's' : ''}? This cannot be undone.`,
                    onConfirm: () => bulkDelete(
                      filteredEmployers.filter(e => selected.has(e.id)).map(e => ({ userId: e.user_id, profileId: e.id, isEmployer: true }))
                    ),
                  })}
                >
                  Delete Selected ({selected.size})
                </button>
              )}
              <span style={s.countBadge}>{filteredEmployers.length} employers</span>
            </div>

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>
                      <input type="checkbox" style={s.checkbox}
                        checked={filteredEmployers.length > 0 && filteredEmployers.every(e => selected.has(e.id))}
                        onChange={() => toggleSelectAll(filteredEmployers.map(e => e.id))}
                      />
                    </th>
                    <th style={s.th}>Company</th>
                    <th style={s.th}>Contact</th>
                    <th style={s.th}>Email</th>
                    <th style={s.th}>Paid</th>
                    <th style={s.th}>Balance</th>
                    <th style={s.th}>Unlocks</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployers.map(e => (
                    <tr key={e.id} style={{ ...s.tr, background: selected.has(e.id) ? '#1a1c2e' : undefined }}>
                      <td style={s.td}>
                        <input type="checkbox" style={s.checkbox}
                          checked={selected.has(e.id)}
                          onChange={() => toggleSelect(e.id)}
                        />
                      </td>
                      <td style={s.td}>
                        <button style={s.linkBtn} onClick={() => setViewingProfile(e)}>{e.company_name}</button>
                      </td>
                      <td style={s.td}>{e.contact_name || '\u2014'}</td>
                      <td style={s.td}><span style={s.emailText}>{e.contact_email || '\u2014'}</span></td>
                      <td style={s.td}>
                        <button
                          style={{
                            ...s.toggleBtnSmall,
                            background: e.is_paid_employer ? '#0a2a1a' : '#1a1c1e',
                            color: e.is_paid_employer ? '#4ade80' : '#7a7570',
                            border: `1px solid ${e.is_paid_employer ? '#4ade8033' : '#2a2c2e'}`,
                          }}
                          disabled={actionLoading === e.user_id}
                          onClick={() => togglePaidEmployer(e.user_id, e.is_paid_employer)}
                        >
                          {e.is_paid_employer ? 'Paid' : 'Free'}
                        </button>
                      </td>
                      <td style={s.td}>
                        {e.balance ? (
                          editingBalance?.employerId === e.id ? (
                            <input
                              type="number"
                              min="0"
                              style={{ ...s.input, width: 60, padding: '4px 8px', fontSize: 13 }}
                              value={editingBalance.value}
                              onChange={ev => setEditingBalance({ employerId: e.id, value: ev.target.value })}
                              onBlur={() => saveUnlockBalance(e.id, editingBalance.value)}
                              onKeyDown={ev => { if (ev.key === 'Enter') saveUnlockBalance(e.id, editingBalance.value); if (ev.key === 'Escape') setEditingBalance(null); }}
                              autoFocus
                            />
                          ) : (
                            <span
                              style={{ cursor: 'pointer', borderBottom: '1px dashed #555' }}
                              onClick={() => setEditingBalance({ employerId: e.id, value: e.balance.current_balance })}
                              title="Click to edit balance"
                            >
                              {e.balance.current_balance}
                            </span>
                          )
                        ) : '\u2014'}
                        {e.balance && !editingBalance?.employerId !== e.id && (
                          <span style={{ color: '#555' }}>/{e.balance.monthly_allowance}</span>
                        )}
                      </td>
                      <td style={s.td}>{e.unlock_count}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button style={s.viewBtnSmall} onClick={() => setViewingProfile(e)}>View</button>
                          <button
                            style={s.deleteBtnSmall}
                            disabled={actionLoading === e.id}
                            onClick={() => setConfirmDelete(e.is_paid_employer ? {
                              message: `This is a paid employer account. Deleting will permanently remove all their job posts, applications, messages, and unlock history.`,
                              onConfirm: () => deleteEmployer(e.id, e.user_id),
                              requireTypedConfirmation: e.company_name || e.contact_email,
                            } : {
                              message: `Delete ${e.company_name}? This will permanently remove their employer profile and all associated data.`,
                              onConfirm: () => deleteEmployer(e.id, e.user_id),
                            })}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredEmployers.length === 0 && (
                <div style={s.emptyTable}>No employer profiles found.</div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ JOB POSTS TAB ═══════════ */}
        {!loading && activeTab === 'jobs' && (
          <div>
            <div style={s.toolbar}>
              <input
                style={s.searchInput}
                placeholder="Search by title or company..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <select style={s.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="published">Published</option>
                <option value="pending_payment">Pending</option>
              </select>
              {selected.size > 0 && (
                <button
                  style={s.bulkDeleteBtn}
                  onClick={() => setConfirmDelete({
                    message: `Delete ${selected.size} job post${selected.size !== 1 ? 's' : ''}? This cannot be undone.`,
                    onConfirm: () => bulkDelete(
                      filteredJobPosts.filter(j => selected.has(j.id)).map(j => ({ type: 'job', id: j.id }))
                    ),
                  })}
                >
                  Delete Selected ({selected.size})
                </button>
              )}
              <span style={s.countBadge}>{filteredJobPosts.length} posts</span>
            </div>

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>
                      <input type="checkbox" style={s.checkbox}
                        checked={filteredJobPosts.length > 0 && filteredJobPosts.every(j => selected.has(j.id))}
                        onChange={() => toggleSelectAll(filteredJobPosts.map(j => j.id))}
                      />
                    </th>
                    <th style={s.th}>Title</th>
                    <th style={s.th}>Company</th>
                    <th style={s.th}>Location</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Posted</th>
                    <th style={s.th}>Applicants</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobPosts.map(j => (
                    <tr key={j.id} style={{ ...s.tr, background: selected.has(j.id) ? '#1a1c2e' : undefined }}>
                      <td style={s.td}>
                        <input type="checkbox" style={s.checkbox}
                          checked={selected.has(j.id)}
                          onChange={() => toggleSelect(j.id)}
                        />
                      </td>
                      <td style={{ ...s.td, fontWeight: 'bold' }}>{j.title}</td>
                      <td style={s.td}>{j.company}</td>
                      <td style={s.td}>{j.location || '\u2014'}</td>
                      <td style={s.td}>
                        <span style={{
                          ...s.typeBadge,
                          background: j.status === 'published' ? '#0a2a1a' : '#2a1a0a',
                          color: j.status === 'published' ? '#4ade80' : '#d97706',
                          border: `1px solid ${j.status === 'published' ? '#4ade8033' : '#d9770633'}`,
                        }}>
                          {j.status === 'pending_payment' ? 'Pending' : j.status}
                        </span>
                      </td>
                      <td style={s.td}>{new Date(j.created_at).toLocaleDateString()}</td>
                      <td style={s.td}>{j.applicant_count}</td>
                      <td style={s.td}>
                        <button
                          style={s.deleteBtnSmall}
                          disabled={actionLoading === j.id}
                          onClick={() => setConfirmDelete({
                            message: `Delete "${j.title}" at ${j.company}? This will also remove all associated applications.`,
                            onConfirm: () => deleteJobPost(j.id),
                          })}
                        >
                          {actionLoading === j.id ? '\u2026' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredJobPosts.length === 0 && (
                <div style={s.emptyTable}>No job posts found.</div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ APPLICATIONS TAB ═══════════ */}
        {!loading && activeTab === 'applications' && (
          <div>
            <div style={s.toolbar}>
              <input
                style={s.searchInput}
                placeholder="Search by tech name, company, or job title..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <select style={s.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="hired">Hired</option>
              </select>
              {selected.size > 0 && (
                <button
                  style={s.bulkDeleteBtn}
                  onClick={() => setConfirmDelete({
                    message: `Delete ${selected.size} application${selected.size !== 1 ? 's' : ''}? This cannot be undone.`,
                    onConfirm: () => bulkDelete(
                      filteredApplications.filter(a => selected.has(a.id)).map(a => ({ type: 'application', id: a.id }))
                    ),
                  })}
                >
                  Delete Selected ({selected.size})
                </button>
              )}
              <span style={s.countBadge}>{filteredApplications.length} applications</span>
            </div>

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>
                      <input type="checkbox" style={s.checkbox}
                        checked={filteredApplications.length > 0 && filteredApplications.every(a => selected.has(a.id))}
                        onChange={() => toggleSelectAll(filteredApplications.map(a => a.id))}
                      />
                    </th>
                    <th style={s.th}>Tech Name</th>
                    <th style={s.th}>Tech Email</th>
                    <th style={s.th}>Job Title</th>
                    <th style={s.th}>Company</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Applied</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map(a => (
                    <tr key={a.id} style={{ ...s.tr, background: selected.has(a.id) ? '#1a1c2e' : undefined }}>
                      <td style={s.td}>
                        <input type="checkbox" style={s.checkbox}
                          checked={selected.has(a.id)}
                          onChange={() => toggleSelect(a.id)}
                        />
                      </td>
                      <td style={{ ...s.td, fontWeight: 'bold' }}>{a.applicant_name || a.tech_name || '\u2014'}</td>
                      <td style={s.td}><span style={s.emailText}>{a.applicant_email || a.tech_email || '\u2014'}</span></td>
                      <td style={s.td}>{a.job_title || '\u2014'}</td>
                      <td style={s.td}>{a.job_company || '\u2014'}</td>
                      <td style={s.td}>
                        <span style={{
                          ...s.typeBadge,
                          background: a.status === 'hired' ? '#0a2a1a' : a.status === 'accepted' ? '#0a2a1a' : a.status === 'rejected' ? '#2a0a0a' : a.status === 'under_review' ? '#1a1a2a' : '#1a1c1e',
                          color: a.status === 'hired' ? '#34d399' : a.status === 'accepted' ? '#4ade80' : a.status === 'rejected' ? '#f87171' : a.status === 'under_review' ? '#60a5fa' : '#9a9490',
                          border: `1px solid ${a.status === 'hired' ? '#34d39933' : a.status === 'accepted' ? '#4ade8033' : a.status === 'rejected' ? '#f8717133' : a.status === 'under_review' ? '#60a5fa33' : '#9a949033'}`,
                        }}>
                          {(a.status || 'submitted').replace('_', ' ')}
                        </span>
                      </td>
                      <td style={s.td}>{new Date(a.created_at).toLocaleDateString()}</td>
                      <td style={s.td}>
                        <button
                          style={s.deleteBtnSmall}
                          disabled={actionLoading === a.id}
                          onClick={() => setConfirmDelete({
                            message: `Delete application from ${a.applicant_name || 'unknown'} for "${a.job_title || 'unknown job'}"?`,
                            onConfirm: () => deleteApplication(a.id),
                          })}
                        >
                          {actionLoading === a.id ? '\u2026' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredApplications.length === 0 && (
                <div style={s.emptyTable}>No applications found.</div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ PLATFORM CONTROLS TAB ═══════════ */}
        {!loading && activeTab === 'platform' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Maintenance Mode */}
            <div style={s.platformCard}>
              <h3 style={s.platformCardTitle}>Maintenance Mode</h3>
              <p style={{ color: '#7a7570', fontSize: 13, margin: '0 0 16px' }}>
                When enabled, non-admin visitors see a maintenance page. Admin dashboard remains accessible.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  style={{
                    ...s.toggleBtnSmall,
                    padding: '8px 20px', fontSize: 14,
                    background: maintenanceMode ? '#2a0a0a' : '#0a2a1a',
                    color: maintenanceMode ? '#f87171' : '#4ade80',
                    border: `1px solid ${maintenanceMode ? '#f8717133' : '#4ade8033'}`,
                  }}
                  onClick={toggleMaintenance}
                >
                  {maintenanceMode ? 'Maintenance ON' : 'Maintenance OFF'}
                </button>
                {maintenanceMode && (
                  <span style={{ fontSize: 12, color: '#f87171' }}>
                    Site is currently showing maintenance page to all visitors
                  </span>
                )}
              </div>
            </div>

            {/* Broadcast Message */}
            <div style={s.platformCard}>
              <h3 style={s.platformCardTitle}>Broadcast Message</h3>
              <p style={{ color: '#7a7570', fontSize: 13, margin: '0 0 12px' }}>
                Send an email announcement to all techs or all employers.
              </p>
              <textarea
                style={{ ...s.input, minHeight: 100, resize: 'vertical', marginBottom: 12 }}
                placeholder="Compose your broadcast message..."
                value={broadcastText}
                onChange={e => setBroadcastText(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  style={{ ...s.btnPrimary, opacity: (!broadcastText.trim() || broadcastSending) ? 0.5 : 1 }}
                  disabled={!broadcastText.trim() || broadcastSending}
                  onClick={() => setBroadcastConfirm('techs')}
                >
                  {broadcastSending ? 'Sending...' : 'Send to All Techs'}
                </button>
                <button
                  style={{ ...s.btnPrimary, background: '#60a5fa', opacity: (!broadcastText.trim() || broadcastSending) ? 0.5 : 1 }}
                  disabled={!broadcastText.trim() || broadcastSending}
                  onClick={() => setBroadcastConfirm('employers')}
                >
                  {broadcastSending ? 'Sending...' : 'Send to All Employers'}
                </button>
              </div>
            </div>

            {/* Export */}
            <div style={s.platformCard}>
              <h3 style={s.platformCardTitle}>Data Export</h3>
              <p style={{ color: '#7a7570', fontSize: 13, margin: '0 0 16px' }}>
                Download CSV exports of platform data.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  style={s.btnPrimary}
                  disabled={exportingTechs}
                  onClick={exportTechCSV}
                >
                  {exportingTechs ? 'Exporting...' : 'Export Tech List'}
                </button>
                <button
                  style={{ ...s.btnPrimary, background: '#60a5fa' }}
                  disabled={exportingEmployers}
                  onClick={exportEmployerCSV}
                >
                  {exportingEmployers ? 'Exporting...' : 'Export Employer List'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ STATS TAB ═══════════ */}
        {!loading && activeTab === 'stats' && stats && (
          <div>
            <div style={s.statsGrid}>
              <StatCard label="Total Users" value={stats.totalUsers} icon={"\uD83D\uDC65"} color="#e8e4dc" />
              <StatCard label="Total Techs" value={stats.totalTechs} icon={"\uD83E\uDE96"} color="#4ade80" />
              <StatCard label="Total Employers" value={stats.totalEmployers} icon={"\uD83C\uDFE2"} color="#60a5fa" />
              <StatCard label="Total Job Posts" value={stats.totalJobPosts} icon={"\uD83D\uDCCB"} color="#d97706" />
              <StatCard label="Total Unlocks" value={stats.totalUnlocks} icon={"\uD83D\uDD13"} color="#a78bfa" />
              <StatCard label="Total Messages" value={stats.totalMessages} icon={"\uD83D\uDCE8"} color="#f472b6" />
              <StatCard label="Total Hires" value={stats.totalHires} icon={"\uD83E\uDD1D"} color="#fbbf24" />
              <StatCard label="New Signups (7d)" value={stats.newSignups} icon={"\uD83D\uDCC8"} color="#34d399" />
            </div>
          </div>
        )}

        {/* ═══════════ RESOURCES TAB ═══════════ */}
        {activeTab === 'resources' && <ResourcesTab />}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div style={s.statCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 12, color: '#7a7570', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            {label}
          </div>
          <div style={{ fontSize: 36, fontWeight: 'bold', color, letterSpacing: '-1px' }}>
            {(value || 0).toLocaleString()}
          </div>
        </div>
        <span style={{ fontSize: 28 }}>{icon}</span>
      </div>
    </div>
  );
}

// ─── Resource Directory management ──────────────────────────────────────────
const BLANK_RESOURCE = {
  id: null, title: '', description: '', category: 'regulations',
  resource_type: 'link', url: '', file_path: '', content: '',
  is_featured: false, is_published: true, sort_order: 0,
};

const catLabel = (id) => RESOURCE_CATEGORIES.find(c => c.id === id)?.label || id;
const typeMeta = (id) => RESOURCE_TYPES.find(t => t.id === id);

function ResourcesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [form, setForm] = useState(null); // null = closed; object = add/edit
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await adminRpc('admin_list_resources');
    if (err) setError(err.message);
    setList(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filtered = list.filter(r =>
    (filterCategory === 'all' || r.category === filterCategory) &&
    (filterType === 'all' || r.resource_type === filterType)
  );

  const updateForm = (patch) => setForm(f => ({ ...f, ...patch }));

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (form.resource_type === 'link' && !form.url.trim()) { setError('A URL is required for link resources.'); return; }
    if (form.resource_type === 'pdf' && !file && !form.file_path) { setError('Please upload a PDF file.'); return; }
    setSaving(true); setError('');
    let filePath = form.file_path || null;
    try {
      if (form.resource_type === 'pdf' && file) {
        // Upload via the upload-resource-pdf edge function, which verifies the
        // admin against admin_users and writes with the service-role key (the
        // resources bucket has had no anon write access since migration 072).
        // The admin key used to ride along as a form field -- the function now
        // rejects a request carrying one.
        const fd = new FormData();
        fd.append('file', file);
        if (form.file_path) fd.append('old_path', form.file_path);
        const { data: upData, error: upErr } = await adminInvoke('upload-resource-pdf', fd);
        if (upErr) throw new Error('File upload failed: ' + upErr.message);
        if (!upData?.path) throw new Error(upData?.error || 'File upload failed.');
        filePath = upData.path;
      }
      const { error: rpcErr } = await adminRpc('admin_upsert_resource', {
        p_id: form.id || null,
        p_title: form.title.trim(),
        p_description: form.description.trim() || null,
        p_category: form.category,
        p_resource_type: form.resource_type,
        p_url: form.resource_type === 'link' ? (form.url.trim() || null) : null,
        p_file_path: form.resource_type === 'pdf' ? filePath : null,
        p_content: form.resource_type === 'article' ? (form.content || null) : null,
        p_is_featured: form.is_featured,
        p_is_published: form.is_published,
        p_sort_order: parseInt(form.sort_order, 10) || 0,
      });
      if (rpcErr) throw rpcErr;
      setForm(null); setFile(null);
      await fetchList();
    } catch (e) {
      setError(e.message || 'Failed to save resource.');
    }
    setSaving(false);
  };

  const handleDelete = async (r) => {
    const { data, error: err } = await adminRpc('admin_delete_resource', {
      p_id: r.id,
    });
    if (err) {
      setError(err.message);
      setConfirmDelete(null);
      await fetchList();
      return;
    }
    if (data?.file_path) {
      const res = await adminDeleteStorageObjects('resources', [data.file_path]);
      if (!res.ok) setError(`Resource deleted, but the PDF was not removed. ${res.error}`);
    }
    setConfirmDelete(null);
    await fetchList();
  };

  const labelStyle = { display: 'block', color: '#9a9490', fontSize: 13, marginBottom: 6, fontWeight: 'bold' };
  const fieldWrap = { marginBottom: 16 };

  return (
    <div>
      {confirmDelete && (
        <ConfirmDialog
          message={`Delete "${confirmDelete.title}"? This cannot be undone${confirmDelete.resource_type === 'pdf' ? ' and will remove the uploaded PDF' : ''}.`}
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {error && (
        <div style={s.errorMsg}>{error} <button style={{ ...s.linkBtn, marginLeft: 8 }} onClick={() => setError('')}>dismiss</button></div>
      )}

      {/* ── Add / Edit form ── */}
      {form ? (
        <div style={{ ...s.platformCard, marginBottom: 24 }}>
          <h3 style={s.platformCardTitle}>{form.id ? 'Edit Resource' : 'Add Resource'}</h3>

          <div style={fieldWrap}>
            <label style={labelStyle}>Title *</label>
            <input style={s.input} value={form.title} onChange={e => updateForm({ title: e.target.value })} placeholder="Resource title" />
          </div>

          <div style={fieldWrap}>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...s.input, minHeight: 64, resize: 'vertical' }} value={form.description} onChange={e => updateForm({ description: e.target.value })} placeholder="Short description (optional)" />
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ ...fieldWrap, flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Category</label>
              <select style={{ ...s.filterSelect, width: '100%' }} value={form.category} onChange={e => updateForm({ category: e.target.value })}>
                {RESOURCE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div style={{ ...fieldWrap, flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Resource Type</label>
              <select style={{ ...s.filterSelect, width: '100%' }} value={form.resource_type} onChange={e => updateForm({ resource_type: e.target.value })}>
                {RESOURCE_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {form.resource_type === 'link' && (
            <div style={fieldWrap}>
              <label style={labelStyle}>URL *</label>
              <input style={s.input} value={form.url} onChange={e => updateForm({ url: e.target.value })} placeholder="https://example.com/document" />
            </div>
          )}

          {form.resource_type === 'pdf' && (
            <div style={fieldWrap}>
              <label style={labelStyle}>PDF File {form.file_path ? '(upload to replace)' : '*'}</label>
              <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files[0] || null)}
                style={{ color: '#9a9490', fontSize: 13, fontFamily: 'inherit' }} />
              {file && <div style={{ fontSize: 12, color: '#4ade80', marginTop: 6 }}>Selected: {file.name}</div>}
              {!file && form.file_path && <div style={{ fontSize: 12, color: '#7a7570', marginTop: 6 }}>Current: {form.file_path.split('/').pop()}</div>}
            </div>
          )}

          {form.resource_type === 'article' && (
            <div style={fieldWrap}>
              <label style={labelStyle}>Article Content</label>
              <textarea style={{ ...s.input, minHeight: 180, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                value={form.content} onChange={e => updateForm({ content: e.target.value })}
                placeholder={"Write the article. Basic formatting:\n# Heading\n## Subheading\n- bullet point\n**bold text**"} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#e8e4dc', fontSize: 14 }}>
              <input type="checkbox" style={s.checkbox} checked={form.is_featured} onChange={e => updateForm({ is_featured: e.target.checked })} />
              Featured
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#e8e4dc', fontSize: 14 }}>
              <input type="checkbox" style={s.checkbox} checked={form.is_published} onChange={e => updateForm({ is_published: e.target.checked })} />
              Published
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#9a9490', fontSize: 14 }}>Sort order</span>
              <input type="number" style={{ ...s.input, width: 90 }} value={form.sort_order} onChange={e => updateForm({ sort_order: e.target.value })} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...s.btnPrimary, ...(saving ? { opacity: 0.5 } : {}) }} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : (form.id ? 'Save Changes' : 'Add Resource')}
            </button>
            <button style={s.btnSecondary} onClick={() => { setForm(null); setFile(null); setError(''); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={s.toolbar}>
          <button style={s.btnPrimary} onClick={() => { setForm({ ...BLANK_RESOURCE }); setFile(null); setError(''); }}>+ Add Resource</button>
          <select style={s.filterSelect} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {RESOURCE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select style={s.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            {RESOURCE_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <span style={s.countBadge}>{filtered.length} resources</span>
        </div>
      )}

      {/* ── List ── */}
      {loading ? (
        <div style={{ color: '#7a7570', textAlign: 'center', padding: 40 }}>Loading...</div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Title</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Type</th>
                <th style={s.th}>Featured</th>
                <th style={s.th}>Published</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td style={s.emptyTable} colSpan={6}>No resources found.</td></tr>
              )}
              {filtered.map(r => (
                <tr key={r.id} style={s.tr}>
                  <td style={s.td}>
                    <div style={{ fontWeight: 'bold' }}>{r.title}</div>
                    {r.description && <div style={{ fontSize: 12, color: '#7a7570', marginTop: 2 }}>{r.description.length > 80 ? r.description.slice(0, 80) + '…' : r.description}</div>}
                  </td>
                  <td style={s.td}><span style={s.miniCert}>{catLabel(r.category)}</span></td>
                  <td style={s.td}>{typeMeta(r.resource_type)?.icon} {typeMeta(r.resource_type)?.label}</td>
                  <td style={s.td}>{r.is_featured ? <span style={{ color: '#fbbf24' }}>{"⭐"} Yes</span> : <span style={{ color: '#7a7570' }}>No</span>}</td>
                  <td style={s.td}>
                    <span style={{
                      ...s.typeBadge,
                      background: r.is_published ? '#0a2a1a' : '#2a1e08',
                      color: r.is_published ? '#4ade80' : '#fbbf24',
                      border: `1px solid ${r.is_published ? '#4ade8033' : '#fbbf2433'}`,
                    }}>
                      {r.is_published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={s.viewBtnSmall} onClick={() => { setForm({ ...BLANK_RESOURCE, ...r, description: r.description || '', url: r.url || '', file_path: r.file_path || '', content: r.content || '', sort_order: r.sort_order ?? 0 }); setFile(null); setError(''); }}>Edit</button>
                      <button style={s.deleteBtnSmall} onClick={() => setConfirmDelete(r)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Admin Documents Section (with optional delete) ─────────────────────────
const ADMIN_DOC_LABELS = {
  resume: 'Resume',
  hazwoper_8hr: 'HAZWOPER 8-Hour Refresher Cert',
  hazwoper_40hr: 'HAZWOPER 40-Hour Cert',
  uxo_eod_cert: 'UXO/EOD Certification',
  physical: 'Physical Exam Cert',
  first_aid_cpr: 'First Aid/CPR Cert',
};

function docExpiryBadge(expiresAt) {
  if (!expiresAt) return { label: 'No Expiration', color: '#4ade80', bg: '#0a2a1a' };
  const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0)   return { label: 'Expired',       color: '#f87171', bg: '#2a0a0a' };
  if (days <= 60) return { label: `${days}d left`,  color: '#fbbf24', bg: '#2a1e08' };
  return { label: 'Active', color: '#4ade80', bg: '#0a2a1a' };
}

function AdminDocSection({ profile, allowDelete }) {
  const [newDocs, setNewDocs] = useState([]);
  const [legacyDocs, setLegacyDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [opening, setOpening] = useState(null);
  const [docError, setDocError] = useState(null);

  // Works for both tech_documents rows and legacy path columns — the edge
  // function signs any path within the uxo-uploads bucket.
  const handleOpenDoc = async (doc, opts) => {
    setDocError(null);
    setOpening(doc.id);
    const { error } = await openAdminDocument(doc.file_path, opts);
    if (error) setDocError(error);
    setOpening(null);
  };

  const buildLegacyDocs = useCallback((prof, newDocTypes) => {
    const old = [];
    if (prof.resume_path && !newDocTypes.has('resume')) {
      old.push({ id: 'old-resume', column: 'resume_path', label: 'Resume', file_path: prof.resume_path, file_name: prof.resume_path.split('/').pop() });
    }
    if (prof.hazwoper8_cert_path && !newDocTypes.has('hazwoper_8hr')) {
      old.push({ id: 'old-hazwoper8', column: 'hazwoper8_cert_path', label: 'HAZWOPER 8-Hour Cert', file_path: prof.hazwoper8_cert_path, file_name: prof.hazwoper8_cert_path.split('/').pop() });
    }
    if (prof.physical_cert_path && !newDocTypes.has('physical')) {
      old.push({ id: 'old-physical', column: 'physical_cert_path', label: 'Physical Cert', file_path: prof.physical_cert_path, file_name: prof.physical_cert_path.split('/').pop() });
    }
    if (prof.cert_paths && prof.cert_paths.length > 0) {
      prof.cert_paths.forEach((p, i) => {
        old.push({ id: `old-cert-${i}`, column: 'cert_paths', arrayIndex: i, label: `Cert Document #${i + 1}`, file_path: p, file_name: p.split('/').pop() });
      });
    }
    return old;
  }, []);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    const { data: rpcData } = await adminRpc('admin_list_tech_documents', {
      target_profile_id: profile.id,
    });
    const data = rpcData || [];

    const mapped = data.map(d => ({
      id: d.id,
      document_type: d.document_type,
      label: ADMIN_DOC_LABELS[d.document_type] || d.document_type,
      file_path: d.file_path,
      file_name: d.file_name,
      uploaded_at: d.uploaded_at,
      expires_at: d.expires_at,
    }));
    setNewDocs(mapped);

    const newDocTypes = new Set(data.map(d => d.document_type));
    setLegacyDocs(buildLegacyDocs(profile, newDocTypes));
    setLoading(false);
  }, [profile, buildLegacyDocs]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleDeleteNewDoc = async (doc) => {
    setDeleting(doc.id);
    setDocError(null);
    const { data, error: rpcErr } = await adminRpc('admin_delete_tech_document', {
      target_document_id: doc.id,
    });
    if (rpcErr) {
      setDocError(`Failed to delete document: ${rpcErr.message}`);
      setDeleting(null);
      return;
    }
    const path = data?.file_path || doc.file_path;
    if (path) {
      const res = await adminDeleteStorageObjects('uxo-uploads', [path]);
      if (!res.ok) setDocError(`Document row deleted, but the file was not removed. ${res.error}`);
    }
    setDeleting(null);
    fetchDocs();
  };

  const handleDeleteLegacyDoc = async (doc) => {
    setDeleting(doc.id);
    setDocError(null);
    // Clear the column via RPC
    const { error: rpcErr } = await adminRpc('admin_clear_legacy_doc', {
      target_profile_id: profile.id,
      column_name: doc.column,
      array_index: doc.arrayIndex != null ? doc.arrayIndex : -1,
    });
    if (rpcErr) {
      setDocError(`Failed to clear document: ${rpcErr.message}`);
      setDeleting(null);
      return;
    }
    // Remove file from storage
    if (doc.file_path) {
      const res = await adminDeleteStorageObjects('uxo-uploads', [doc.file_path]);
      if (!res.ok) setDocError(`Reference cleared, but the file was not removed. ${res.error}`);
    }
    // Update local legacy docs list immediately
    setLegacyDocs(prev => prev.filter(d => d.id !== doc.id));
    setDeleting(null);
  };

  if (loading) return <p style={{ color: '#7a7570', fontSize: 13, marginBottom: 12 }}>Loading documents...</p>;

  const totalCount = newDocs.length + legacyDocs.length;
  if (totalCount === 0) return (
    <div style={{ background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Documents</div>
      <p style={{ color: '#7a7570', fontSize: 13, margin: 0 }}>No documents uploaded.</p>
    </div>
  );

  const renderDocRow = (doc, onDelete) => {
    const badge = doc.expires_at ? docExpiryBadge(doc.expires_at) : (doc.document_type ? docExpiryBadge(null) : null);
    return (
      <div key={doc.id} style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#111316', border: '1px solid #1e2022', borderRadius: 6,
        padding: '8px 12px', flexWrap: 'wrap', gap: 6,
      }}>
        <div style={{ flex: 1, minWidth: 150 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 'bold', color: '#e8e4dc' }}>{doc.label}</span>
            {badge && (
              <span style={{
                fontSize: 10, padding: '2px 6px', borderRadius: 8,
                background: badge.bg, color: badge.color, fontWeight: 'bold',
                border: `1px solid ${badge.color}33`,
              }}>
                {badge.label}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#7a7570' }}>
            {doc.file_name}
            {doc.uploaded_at && ` \u00B7 ${new Date(doc.uploaded_at).toLocaleDateString()}`}
            {doc.expires_at && ` \u00B7 Expires ${new Date(doc.expires_at).toLocaleDateString()}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            style={{
              background: 'none', border: '1px solid #60a5fa44', color: '#60a5fa',
              padding: '3px 8px', borderRadius: 4,
              cursor: doc.file_path ? 'pointer' : 'not-allowed',
              fontSize: 11, fontFamily: 'inherit', opacity: doc.file_path ? 1 : 0.4,
            }}
            disabled={!doc.file_path || opening === doc.id}
            onClick={() => handleOpenDoc(doc)}
          >
            {opening === doc.id ? '...' : 'View'}
          </button>
          <button
            style={{
              background: 'none', border: '1px solid #60a5fa44', color: '#60a5fa',
              padding: '3px 8px', borderRadius: 4,
              cursor: doc.file_path ? 'pointer' : 'not-allowed',
              fontSize: 11, fontFamily: 'inherit', opacity: doc.file_path ? 1 : 0.4,
            }}
            disabled={!doc.file_path || opening === doc.id}
            onClick={() => handleOpenDoc(doc, { download: true })}
          >
            Download
          </button>
          {allowDelete && (
            <button
              style={{
                background: 'none', border: '1px solid #f8717133', color: '#f87171',
                padding: '3px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 11,
                fontFamily: 'inherit',
              }}
              disabled={deleting === doc.id}
              onClick={() => onDelete(doc)}
            >
              {deleting === doc.id ? '...' : 'Delete'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {docError && (
        <div style={{
          background: '#2a0a0a', border: '1px solid #f8717133', borderRadius: 6,
          padding: '8px 12px', marginBottom: 12, color: '#f87171', fontSize: 12,
        }}>
          {docError}
        </div>
      )}
      {newDocs.length > 0 && (
        <div style={{ background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Documents ({newDocs.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {newDocs.map(doc => renderDocRow(doc, handleDeleteNewDoc))}
          </div>
        </div>
      )}

      {legacyDocs.length > 0 && (
        <div style={{ background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Legacy Documents ({legacyDocs.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {legacyDocs.map(doc => renderDocRow(doc, handleDeleteLegacyDoc))}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Admin Unlocked Techs Section ───────────────────────────────────────────
function AdminUnlockedTechs({ employerId }) {
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  const loadTechs = async () => {
    setLoading(true);
    const { data } = await adminRpc('admin_list_employer_unlocks', {
      target_employer_id: employerId,
    });
    setTechs(data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadTechs();
  }, [employerId]); // eslint-disable-line

  const handleRemoveUnlock = async (tech) => {
    if (!window.confirm(`Remove unlock for ${tech.name || 'this tech'}? This will hide their contact info from this employer.`)) return;
    setRemovingId(tech.tech_profile_id);
    const { error } = await adminRpc('admin_remove_unlock', {
      target_employer_id: employerId,
      target_tech_profile_id: tech.tech_profile_id,
    });
    setRemovingId(null);
    if (error) {
      console.error('admin_remove_unlock failed:', error);
      alert('Failed to remove unlock: ' + error.message);
      return;
    }
    await loadTechs();
  };

  if (loading) return <p style={{ color: '#7a7570', fontSize: 13, marginBottom: 12 }}>Loading unlocked techs...</p>;

  return (
    <div style={{ background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Unlocked Techs ({techs.length})
      </div>
      {techs.length === 0 ? (
        <p style={{ color: '#7a7570', fontSize: 13, margin: 0 }}>No techs unlocked yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {techs.map((t, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#111316', border: '1px solid #1e2022', borderRadius: 6,
              padding: '6px 12px',
            }}>
              <div>
                <span style={{ fontSize: 13, color: '#e8e4dc', fontWeight: 'bold' }}>{t.name || 'Unknown'}</span>
                {t.location && <span style={{ fontSize: 11, color: '#7a7570', marginLeft: 8 }}>{t.location}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: '#555' }}>
                  {t.unlocked_at && new Date(t.unlocked_at).toLocaleDateString()}
                </span>
                <button
                  onClick={() => handleRemoveUnlock(t)}
                  disabled={removingId === t.tech_profile_id}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 6,
                    background: 'transparent', border: '1px solid #4a1f1f',
                    color: '#f87171', cursor: removingId === t.tech_profile_id ? 'default' : 'pointer',
                    opacity: removingId === t.tech_profile_id ? 0.5 : 1,
                    fontFamily: "'Georgia','Times New Roman',serif",
                  }}
                >
                  {removingId === t.tech_profile_id ? 'Removing...' : 'Remove Unlock'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminEmployerInvitations({ employerId }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await adminRpc('admin_list_employer_invitations', {
        target_employer_id: employerId,
      });
      setInvites(data || []);
      setLoading(false);
    })();
  }, [employerId]);

  if (loading) return <p style={{ color: '#7a7570', fontSize: 13, marginBottom: 12 }}>Loading invitations...</p>;

  return (
    <div style={{ background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Invitations Sent ({invites.length})
      </div>
      {invites.length === 0 ? (
        <p style={{ color: '#7a7570', fontSize: 13, margin: 0 }}>No invitations sent.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {invites.map((inv, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#111316', border: '1px solid #1e2022', borderRadius: 6,
              padding: '6px 12px',
            }}>
              <div>
                <span style={{ fontSize: 13, color: '#e8e4dc', fontWeight: 'bold' }}>{inv.tech_name || 'Unknown'}</span>
                <span style={{ fontSize: 11, color: '#7a7570', marginLeft: 8 }}>{inv.job_title || 'Unknown job'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 8,
                  background: inv.status === 'accepted' ? '#0a2a1a' : inv.status === 'declined' ? '#2a0a0a' : '#1a1c1e',
                  color: inv.status === 'accepted' ? '#4ade80' : inv.status === 'declined' ? '#f87171' : '#9a9490',
                }}>{inv.status}</span>
                <span style={{ fontSize: 11, color: '#555' }}>
                  {inv.created_at && new Date(inv.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminTechInvitations({ techId }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await adminRpc('admin_list_tech_invitations', {
        target_tech_id: techId,
      });
      setInvites(data || []);
      setLoading(false);
    })();
  }, [techId]);

  if (loading) return <p style={{ color: '#7a7570', fontSize: 13, marginBottom: 12 }}>Loading invitations...</p>;

  return (
    <div style={{ background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Invitations Received ({invites.length})
      </div>
      {invites.length === 0 ? (
        <p style={{ color: '#7a7570', fontSize: 13, margin: 0 }}>No invitations received.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {invites.map((inv, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#111316', border: '1px solid #1e2022', borderRadius: 6,
              padding: '6px 12px',
            }}>
              <div>
                <span style={{ fontSize: 13, color: '#e8e4dc', fontWeight: 'bold' }}>{inv.company_name || 'Unknown'}</span>
                <span style={{ fontSize: 11, color: '#7a7570', marginLeft: 8 }}>{inv.job_title || 'Unknown job'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 8,
                  background: inv.status === 'accepted' ? '#0a2a1a' : inv.status === 'declined' ? '#2a0a0a' : '#1a1c1e',
                  color: inv.status === 'accepted' ? '#4ade80' : inv.status === 'declined' ? '#f87171' : '#9a9490',
                }}>{inv.status}</span>
                <span style={{ fontSize: 11, color: '#555' }}>
                  {inv.created_at && new Date(inv.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = {
  root: { fontFamily: "'Georgia','Times New Roman',serif", background: '#0d0f10', color: '#e8e4dc', minHeight: '100vh' },

  // Login
  loginWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 },
  loginCard: { background: '#111316', border: '1px solid #1e2022', borderRadius: 12, padding: '48px 40px', maxWidth: 400, width: '100%' },
  passwordWrap: { position: 'relative', display: 'flex', alignItems: 'stretch' },
  eyeBtn: { marginLeft: -48, width: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, position: 'relative', zIndex: 2 },
  input: { background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 6, color: '#e8e4dc', padding: '12px 16px', fontSize: 15, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  errorMsg: { background: '#2a0a0a', border: '1px solid #8B0000', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#ff6b6b', marginTop: 10 },
  actionErrorBanner: { position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10000, background: '#2a0a0a', border: '1px solid #8B0000', borderRadius: 8, padding: '12px 20px', fontSize: 14, color: '#ff6b6b', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' },
  btnPrimary: { background: '#d97706', border: 'none', color: '#0d0f10', padding: '12px 24px', fontSize: 15, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' },
  btnSecondary: { background: 'none', border: '1px solid #333', color: '#9a9490', padding: '8px 18px', fontSize: 13, borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' },
  btnDanger: { background: '#dc2626', border: 'none', color: '#fff', padding: '8px 18px', fontSize: 13, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' },

  // Header
  header: { background: '#111316', borderBottom: '1px solid #222', position: 'sticky', top: 0, zIndex: 100 },
  headerInner: { maxWidth: 1100, margin: '0 auto', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
  headerLabel: { fontSize: 11, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 },
  headerName: { fontSize: 20, fontWeight: 'bold', letterSpacing: '-0.3px' },
  logoutBtn: { background: 'none', border: '1px solid #333', color: '#9a9490', cursor: 'pointer', padding: '6px 14px', fontSize: 13, borderRadius: 6, fontFamily: 'inherit' },

  // Tabs
  tabBar: { maxWidth: 1100, margin: '0 auto', padding: '12px 20px 0', display: 'flex', gap: 4, borderBottom: '1px solid #1e2022', overflowX: 'auto' },
  tab: { background: 'none', border: 'none', color: '#9a9590', cursor: 'pointer', padding: '8px 16px 12px', fontSize: 15, fontFamily: 'inherit', borderBottom: '2px solid transparent', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
  tabActive: { color: '#e8e4dc', borderBottom: '2px solid #d97706', fontWeight: 'bold' },

  // Body
  body: { maxWidth: 1100, margin: '0 auto', padding: '28px 20px 60px' },

  // Toolbar
  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  searchInput: { background: '#161819', border: '1px solid #2a2c2e', color: '#e8e4dc', padding: '9px 14px', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', outline: 'none', minWidth: 240, flex: 1, maxWidth: 400 },
  filterSelect: { background: '#161819', border: '1px solid #2a2c2e', color: '#e8e4dc', padding: '9px 14px', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' },
  countBadge: { color: '#6a6660', fontSize: 13, marginLeft: 'auto' },

  // Table
  tableWrap: { overflowX: 'auto', borderRadius: 10, border: '1px solid #1e2022' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#111316' },
  th: { textAlign: 'left', padding: '12px 16px', fontSize: 11, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #1e2022', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #1a1c1e' },
  td: { padding: '12px 16px', fontSize: 14, verticalAlign: 'middle' },
  emptyTable: { padding: 40, textAlign: 'center', color: '#7a7570', background: '#111316' },

  // Buttons
  deleteBtnSmall: { background: 'none', border: '1px solid #f8717133', color: '#f87171', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  viewBtnSmall: { background: 'none', border: '1px solid #60a5fa33', color: '#60a5fa', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  toggleBtnSmall: { padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', fontWeight: 'bold', whiteSpace: 'nowrap' },
  linkBtn: { background: 'none', border: 'none', color: '#d97706', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit', padding: 0, textAlign: 'left', fontWeight: 'bold' },
  emailText: { fontSize: 13, color: '#9a9490', wordBreak: 'break-all' },
  typeBadge: { fontSize: 11, padding: '3px 9px', borderRadius: 12, fontWeight: 'bold', whiteSpace: 'nowrap' },
  miniCert: { fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#1a1408', color: '#d97706', border: '1px solid #d9770633', whiteSpace: 'nowrap' },

  // Overlay & Dialog
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  dialog: { background: '#111316', border: '1px solid #1e2022', borderRadius: 12, padding: '28px 32px', maxWidth: 440, width: '100%' },
  profileModal: { background: '#111316', border: '1px solid #1e2022', borderRadius: 12, padding: '28px 32px', maxWidth: 600, width: '100%', maxHeight: '80vh' },
  closeBtn: { background: 'none', border: 'none', color: '#9a9490', cursor: 'pointer', fontSize: 18, padding: '4px 8px' },
  profilePre: { background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: 16, fontSize: 12, color: '#9a9490', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', lineHeight: 1.6 },

  // Bulk delete
  checkbox: { accentColor: '#d97706', width: 16, height: 16, cursor: 'pointer' },
  bulkDeleteBtn: { background: '#dc2626', border: 'none', color: '#fff', padding: '8px 16px', fontSize: 13, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  successBanner: { position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10000, background: '#0a2a1a', border: '1px solid #4ade8044', borderRadius: 8, padding: '12px 20px', fontSize: 14, color: '#4ade80', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' },
  progressTrack: { background: '#1a1c1e', borderRadius: 4, height: 8, overflow: 'hidden' },
  progressBar: { background: '#d97706', height: '100%', borderRadius: 4, transition: 'width 0.2s ease' },

  // Stats
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 16 },
  statCard: { background: '#111316', border: '1px solid #1e2022', borderRadius: 10, padding: '24px 28px' },

  // Platform controls
  platformCard: { background: '#111316', border: '1px solid #1e2022', borderRadius: 10, padding: '24px 28px' },
  platformCardTitle: { fontSize: 18, fontWeight: 'bold', margin: '0 0 8px', color: '#e8e4dc' },
};
